import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, Briefcase, ArrowLeft, Euro, X, Upload, Download, Paperclip, FolderKanban, ArrowRight, FileText, Eye, FileSpreadsheet, Check, AlertCircle, Loader2, Search, Filter, RotateCcw } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const OrdiniLavoroPage = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [ordini, setOrdini] = useState([]);
  const [clienti, setClienti] = useState([]);
  const [utenti, setUtenti] = useState([]);
  const [preventivi, setPreventivi] = useState([]);
  const [preventiviAccettati, setPreventiviAccettati] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('ordini'); // 'ordini' o 'preventivi'
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingOrdine, setEditingOrdine] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedOrdine, setSelectedOrdine] = useState(null);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDescrizione, setUploadDescrizione] = useState('');
  const [descrizioniSAL, setDescrizioniSAL] = useState([]);
  const [nuovaDescrizioneSAL, setNuovaDescrizioneSAL] = useState('');
  const [dialogDescrizioneSAL, setDialogDescrizioneSAL] = useState({ open: false, salIndex: null });
  const [inputNuovaDescrizioneSAL, setInputNuovaDescrizioneSAL] = useState('');
  const [notifiche, setNotifiche] = useState([]);
  const [ordiniConNotifiche, setOrdiniConNotifiche] = useState(new Set());
  
  // Excel Import States
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importStep, setImportStep] = useState(1); // 1: upload, 2: mapping, 3: preview/edit, 4: result
  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importMappings, setImportMappings] = useState({});
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [defaultPmId, setDefaultPmId] = useState('');
  const [editableRows, setEditableRows] = useState([]); // Righe editabili per step 3
  const [editPage, setEditPage] = useState(1); // Paginazione per step 3
  const ROWS_PER_PAGE = 25; // Righe per pagina
  const [salEditRowId, setSalEditRowId] = useState(null); // ID riga per editing SAL
  
  // Filtri per la tabella ordini
  const [filters, setFilters] = useState({
    searchText: '',
    clienteId: '',
    pmId: '',
    pmClienteFilter: '',
    statoFatturazione: '',
    tipologiaLavorazione: '',
    dataInizio: '',
    dataFine: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [allegatiNuovoOrdine, setAllegatiNuovoOrdine] = useState([]); // Allegati per nuovo ordine
  const [allegatiQuickViewOpen, setAllegatiQuickViewOpen] = useState(false);
  const [allegatiQuickViewOrdine, setAllegatiQuickViewOrdine] = useState(null);
  
  const [formData, setFormData] = useState({
    cliente_id: '',
    referente_cliente: '',
    pm_cliente: '',
    codice_progetto_cliente: '',
    protocollo_ordine_lavoro: '',  // Nuovo campo
    codice_progetto_main_solution: '',
    nome_progetto: '',
    tipologia_lavorazione: '',
    descrizione_attivita: '',
    data_arrivo_ordine: '',
    data_conferma_ordine: '',
    data_inizio_lavoro: '',
    data_fine_lavoro: '',
    pm_main_solution_id: '',
    importo_totale: '',
    sal_list: [],
    preventivo_id: '',
    multiple_commesse: false,
    crea_progetto_automaticamente: false,
    crea_cantiere_automaticamente: false
  });
  
  // Lista tipologie di lavorazione (come nei preventivi)
  const TIPOLOGIE_LAVORAZIONE = [
    'Consulenza',
    'Progettazione definitiva',
    'Progettazione esecutiva',
    'Incarichi Professionali',
    'EPC B2B',
    'EPC B2C',
    'Contratto Quadro',
    'Offerte Spot'
  ];

  // Helper per verificare se un utente ha un determinato ambito (supporta ambiti multipli)
  const userHasAmbito = (u, ambito) => {
    if (u.ambiti && u.ambiti.length > 0) {
      return u.ambiti.includes(ambito);
    }
    return u.ambito === ambito;
  };

  useEffect(() => {
    fetchOrdini();
    fetchClienti();
    fetchUtenti();
    fetchPreventiviDisponibili();
    fetchPreventiviAccettati();
    fetchNotifiche();
    
    // Carica descrizioni SAL salvate
    const saved = localStorage.getItem('descrizioniSAL');
    if (saved) {
      setDescrizioniSAL(JSON.parse(saved));
    } else {
      // Descrizioni predefinite
      setDescrizioniSAL([
        'Acconto',
        'SAL Intermedio',
        'SAL Finale',
        'Saldo',
        'Materiali',
        'Manodopera',
        'Progettazione',
        'Collaudo'
      ]);
    }
  }, []);


  // Apri dialog automaticamente se c'è parametro openDialog nell'URL
  useEffect(() => {
    const openDialogId = searchParams.get('openDialog');
    if (openDialogId && ordini.length > 0) {
      const ordineToOpen = ordini.find(o => o.id === openDialogId);
      if (ordineToOpen) {
        console.log('Opening dialog for ordine:', ordineToOpen.numero_ordine);
        setSelectedOrdine(ordineToOpen);
        setDetailDialogOpen(true);
        // Rimuovi il parametro dall'URL dopo un breve delay
        setTimeout(() => setSearchParams({}), 500);
      }
    }
  }, [searchParams, ordini, setSearchParams]);


  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return {
      headers: {
        Authorization: `Bearer ${token}`
      }
    };
  };

  const fetchOrdini = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/ordini-lavoro`, getAuthHeaders());
      setOrdini(response.data);
    } catch (error) {
      console.error('Errore nel caricamento ordini:', error);
      toast.error('Errore nel caricamento degli ordini');
    } finally {
      setLoading(false);
    }
  };

  const fetchClienti = async () => {
    try {
      const response = await axios.get(`${API}/clienti`, getAuthHeaders());
      setClienti(response.data);
    } catch (error) {
      console.error('Errore caricamento clienti:', error);
    }
  };

  const fetchUtenti = async () => {
    try {
      const response = await axios.get(`${API}/users`, getAuthHeaders());
      setUtenti(response.data);
    } catch (error) {
      console.error('Errore caricamento utenti:', error);
    }
  };

  const fetchPreventiviDisponibili = async () => {
    try {
      const response = await axios.get(`${API}/preventivi/accettati/disponibili`, getAuthHeaders());
      setPreventivi(response.data || []);
    } catch (error) {
      console.error('Errore caricamento preventivi:', error);
      setPreventivi([]);
    }
  };

  const fetchPreventiviAccettati = async () => {
    try {
      const response = await axios.get(`${API}/preventivi`, getAuthHeaders());
      // Filtra solo preventivi accettati NON ancora convertiti
      const accettati = response.data.filter(p => 
        p.stato.toLowerCase() === 'accettato' && !p.ordine_lavoro_id
      );
      setPreventiviAccettati(accettati);
    } catch (error) {
      console.error('Errore caricamento preventivi accettati:', error);
      setPreventiviAccettati([]);
    }
  };

  const fetchNotifiche = async () => {
    // Per amministrativi, admin e master
    if (user.ambito === 'amministrativo' || user.ruolo === 'admin' || user.ruolo === 'master') {
      try {
        const response = await axios.get(`${API}/notifiche`, getAuthHeaders());
        const notificheNonLette = response.data.filter(n => !n.letta);
        setNotifiche(notificheNonLette);
        
        // Crea set di ordini che hanno notifiche
        const ordiniIds = new Set(notificheNonLette.map(n => n.ordine_lavoro_id));
        setOrdiniConNotifiche(ordiniIds);
      } catch (error) {
        console.error('Errore caricamento notifiche:', error);
      }
    }
  };


  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddSAL = () => {
    setFormData(prev => ({
      ...prev,
      sal_list: [...prev.sal_list, { 
        numero_sal: prev.sal_list.length + 1,
        descrizione: '',
        percentuale: 0, 
        importo: 0, 
        stato: 'non_fatturabile',  // Default: Non Fatturabile
        riferimento_fattura: '',  // OdA o riferimento da inserire in fattura
        termini_pagamento_giorni: 30,  // Default: 30 giorni
        fattura_fine_mese: false  // Default: false
      }]
    }));
  };

  const handleAggiungiDescrizioneSAL = () => {
    if (!nuovaDescrizioneSAL.trim()) {
      toast.error('Inserisci una descrizione');
      return;
    }
    
    if (descrizioniSAL.includes(nuovaDescrizioneSAL.trim())) {
      toast.error('Questa descrizione esiste già');
      return;
    }
    
    const nuoveDescrizioni = [...descrizioniSAL, nuovaDescrizioneSAL.trim()];
    setDescrizioniSAL(nuoveDescrizioni);
    localStorage.setItem('descrizioniSAL', JSON.stringify(nuoveDescrizioni));
    toast.success('Descrizione aggiunta!');
    setNuovaDescrizioneSAL('');
  };

  const calculateSALImporto = (percentuale) => {
    const importoTotale = parseFloat(formData.importo_totale) || 0;
    return (importoTotale * parseFloat(percentuale)) / 100;
  };

  const handleRemoveSAL = (index) => {
    const sal = formData.sal_list[index];
    const salInfo = sal.descrizione ? `SAL ${sal.numero_sal} - ${sal.descrizione}` : `SAL ${sal.numero_sal}`;
    
    if (!window.confirm(`Sei sicuro di voler eliminare "${salInfo}"?`)) {
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      sal_list: prev.sal_list.filter((_, i) => i !== index)
    }));
  };

  const handleSALChange = (index, field, value) => {
    setFormData(prev => {
      const newSalList = [...prev.sal_list];
      newSalList[index] = { ...newSalList[index], [field]: value };
      
      // Se cambia la percentuale, ricalcola l'importo
      if (field === 'percentuale') {
        newSalList[index].importo = calculateSALImporto(value);
      }
      
      return { ...prev, sal_list: newSalList };
    });
  };

  const resetForm = () => {
    setFormData({
      cliente_id: '',
      referente_cliente: '',
      pm_cliente: '',
      codice_progetto_cliente: '',
      protocollo_ordine_lavoro: '',
      codice_progetto_main_solution: '',
      nome_progetto: '',
      tipologia_lavorazione: '',
      descrizione_attivita: '',
      data_arrivo_ordine: '',
      data_conferma_ordine: '',
      data_inizio_lavoro: '',
      data_fine_lavoro: '',
      pm_main_solution_id: '',
      importo_totale: '',
      sal_list: [],
      preventivo_id: '',
      multiple_commesse: false,
      crea_progetto_automaticamente: false,
      crea_cantiere_automaticamente: false
    });
    setEditingOrdine(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const submitData = {
        ...formData,
        importo_totale: parseFloat(formData.importo_totale),
        sal_list: formData.sal_list.map(sal => ({
          ...sal,
          percentuale: parseFloat(sal.percentuale),
          importo: parseFloat(sal.importo)
        }))
      };

      if (editingOrdine) {
        await axios.put(`${API}/ordini-lavoro/${editingOrdine.id}`, submitData, getAuthHeaders());
        toast.success('Ordine aggiornato con successo');
      } else {
        // Crea l'ordine
        const response = await axios.post(`${API}/ordini-lavoro`, submitData, getAuthHeaders());
        const nuovoOrdineId = response.data.id;
        toast.success('Ordine creato con successo');
        
        // Carica gli allegati se presenti
        if (allegatiNuovoOrdine.length > 0) {
          let allegatiCaricati = 0;
          for (const file of allegatiNuovoOrdine) {
            try {
              const fileFormData = new FormData();
              fileFormData.append('file', file);
              await axios.post(
                `${API}/ordini-lavoro/${nuovoOrdineId}/allegati?descrizione=${encodeURIComponent(file.name)}`,
                fileFormData,
                {
                  ...getAuthHeaders(),
                  headers: {
                    ...getAuthHeaders().headers,
                    'Content-Type': 'multipart/form-data'
                  }
                }
              );
              allegatiCaricati++;
            } catch (allegatoError) {
              console.error('Errore caricamento allegato:', file.name, allegatoError);
            }
          }
          if (allegatiCaricati > 0) {
            toast.success(`${allegatiCaricati} allegat${allegatiCaricati === 1 ? 'o caricato' : 'i caricati'}`);
          }
        }
        
        // Se era una conversione da preventivo, ricarica la lista preventivi
        if (submitData.preventivo_id) {
          fetchPreventiviAccettati();
          toast.success('Preventivo convertito in ordine!');
        }
      }
      
      setDialogOpen(false);
      resetForm();
      setAllegatiNuovoOrdine([]); // Reset allegati
      fetchOrdini();
    } catch (error) {
      console.error('Errore nel salvataggio:', error);
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio dell\'ordine');
    }
  };

  const handleEdit = (ordine) => {
    setEditingOrdine(ordine);
    setFormData({
      cliente_id: ordine.cliente_id,
      referente_cliente: ordine.referente_cliente,
      pm_cliente: ordine.pm_cliente,
      codice_progetto_cliente: ordine.codice_progetto_cliente,
      protocollo_ordine_lavoro: ordine.protocollo_ordine_lavoro || '',
      codice_progetto_main_solution: ordine.codice_progetto_main_solution,
      nome_progetto: ordine.nome_progetto,
      tipologia_lavorazione: ordine.tipologia_lavorazione || '',
      descrizione_attivita: ordine.descrizione_attivita,
      data_arrivo_ordine: ordine.data_arrivo_ordine,
      data_conferma_ordine: ordine.data_conferma_ordine || '',
      data_inizio_lavoro: ordine.data_inizio_lavoro || '',
      data_fine_lavoro: ordine.data_fine_lavoro || '',
      pm_main_solution_id: ordine.pm_main_solution_id,
      importo_totale: ordine.importo_totale.toString(),
      sal_list: ordine.sal_list || [],
      multiple_commesse: ordine.multiple_commesse || false
    });
    setDialogOpen(true);
  };

  const handleDelete = async (ordineId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo ordine?')) return;
    
    try {
      await axios.delete(`${API}/ordini-lavoro/${ordineId}`, getAuthHeaders());
      toast.success('Ordine eliminato con successo');
      fetchOrdini();
    } catch (error) {
      console.error('Errore nell\'eliminazione:', error);
      toast.error('Errore nell\'eliminazione dell\'ordine');
    }
  };



  const handleUploadAllegato = async (ordineId) => {
    if (!uploadFile) {
      toast.error('Seleziona un file');
      return;
    }

    const formData = new FormData();
    formData.append('file', uploadFile);
    formData.append('descrizione', uploadDescrizione);

    try {
      await axios.post(
        `${API}/ordini-lavoro/${ordineId}/allegati?descrizione=${encodeURIComponent(uploadDescrizione)}`,
        formData,
        {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      toast.success('Allegato caricato con successo');
      setUploadFile(null);
      setUploadDescrizione('');
      fetchOrdini();
      // Refresh selected ordine
      const response = await axios.get(`${API}/ordini-lavoro/${ordineId}`, getAuthHeaders());
      setSelectedOrdine(response.data);
    } catch (error) {
      console.error('Errore upload allegato:', error);
      toast.error('Errore nel caricamento dell\'allegato');
    }
  };

  const handleDownloadAllegato = async (ordineId, allegatoId, nomeFile) => {
    try {
      toast.info('Download in corso...', { duration: 2000 });
      
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API}/ordini-lavoro/${ordineId}/allegati/${allegatoId}/download`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Errore nel download';
        
        if (response.status === 404) {
          errorMessage = 'File non trovato. Il file potrebbe essere stato eliminato o non è ancora stato sincronizzato con SharePoint.';
        } else {
          try {
            const errorData = JSON.parse(errorText);
            errorMessage = errorData.detail || errorMessage;
          } catch {
            // Non è JSON
          }
        }
        throw new Error(errorMessage);
      }
      
      // Scarica come blob
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', nomeFile || 'allegato');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Download completato!');
    } catch (error) {
      console.error('Errore download:', error);
      toast.error(error.message || 'Errore nel download dell\'allegato');
    }
  };

  const handleDeleteAllegato = async (ordineId, allegatoId) => {
    if (!window.confirm('Sei sicuro di voler eliminare questo allegato?')) return;

    try {
      await axios.delete(`${API}/ordini-lavoro/${ordineId}/allegati/${allegatoId}`, getAuthHeaders());
      toast.success('Allegato eliminato');
      fetchOrdini();
      // Refresh selected ordine
      const response = await axios.get(`${API}/ordini-lavoro/${ordineId}`, getAuthHeaders());
      setSelectedOrdine(response.data);
    } catch (error) {
      console.error('Errore eliminazione allegato:', error);
      toast.error('Errore nell\'eliminazione dell\'allegato');
    }
  };

  // Elimina SAL dalla vista dettagli (aggiorna direttamente sul server)
  const handleDeleteSALFromDetails = async (ordineId, salIndex, salInfo) => {
    if (!window.confirm(`Sei sicuro di voler eliminare "${salInfo}"?\n\nQuesta azione è irreversibile.`)) return;

    try {
      // Recupera l'ordine attuale
      const ordine = selectedOrdine;
      
      // Filtra la sal_list rimuovendo il SAL all'indice specificato
      const updatedSalList = ordine.sal_list.filter((_, i) => i !== salIndex);
      
      // Rinumera i SAL
      const renumberedSalList = updatedSalList.map((sal, i) => ({
        ...sal,
        numero_sal: i + 1
      }));

      // Aggiorna l'ordine sul server
      await axios.put(
        `${API}/ordini-lavoro/${ordineId}`,
        {
          ...ordine,
          sal_list: renumberedSalList
        },
        getAuthHeaders()
      );

      toast.success('SAL eliminato con successo');
      
      // Refresh data
      fetchOrdini();
      const response = await axios.get(`${API}/ordini-lavoro/${ordineId}`, getAuthHeaders());
      setSelectedOrdine(response.data);
    } catch (error) {
      console.error('Errore eliminazione SAL:', error);
      toast.error('Errore nell\'eliminazione del SAL');
    }
  };

  const handleViewDetails = async (ordine) => {
    setSelectedOrdine(ordine);
    setDetailDialogOpen(true);
  };

  const handleConvertiInProgetto = async (ordineId, numeroOrdine) => {
    if (!window.confirm(`Vuoi convertire l'ordine ${numeroOrdine} in un progetto?`)) {
      return;
    }

    try {
      const response = await axios.post(
        `${API}/ordini-lavoro/${ordineId}/converti-progetto`,
        {},
        getAuthHeaders()
      );
      
      toast.success(`Progetto ${response.data.progetto_numero} creato con successo!`);
      fetchOrdini(); // Ricarica la lista per mostrare il badge
    } catch (error) {
      console.error('Errore conversione in progetto:', error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error('Errore nella conversione in progetto');
      }
    }
  };

  const handleDialogOpenChange = (open) => {
    setDialogOpen(open);
    if (!open) {
      resetForm();
      setAllegatiNuovoOrdine([]); // Reset allegati quando si chiude il dialog
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  // ============================================
  // FILTRI ORDINI
  // ============================================
  
  // Funzione per filtrare gli ordini
  const getFilteredOrdini = () => {
    return ordini.filter(ordine => {
      // Filtro ricerca testuale (numero ordine, cliente, progetto, codici)
      if (filters.searchText) {
        const searchLower = filters.searchText.toLowerCase();
        const matchesSearch = 
          (ordine.numero_ordine && ordine.numero_ordine.toLowerCase().includes(searchLower)) ||
          (ordine.cliente_nome && ordine.cliente_nome.toLowerCase().includes(searchLower)) ||
          (ordine.nome_progetto && ordine.nome_progetto.toLowerCase().includes(searchLower)) ||
          (ordine.codice_progetto_cliente && ordine.codice_progetto_cliente.toLowerCase().includes(searchLower)) ||
          (ordine.codice_progetto_main_solution && ordine.codice_progetto_main_solution.toLowerCase().includes(searchLower));
        if (!matchesSearch) return false;
      }
      
      // Filtro cliente
      if (filters.clienteId && ordine.cliente_id !== filters.clienteId) {
        return false;
      }
      
      // Filtro PM Main Solution
      if (filters.pmId && ordine.pm_main_solution_id !== filters.pmId) {
        return false;
      }
      
      // Filtro PM Cliente
      if (filters.pmClienteFilter) {
        const pmClienteLower = (ordine.pm_cliente || '').toLowerCase();
        if (!pmClienteLower.includes(filters.pmClienteFilter.toLowerCase())) {
          return false;
        }
      }
      
      // Filtro tipologia lavorazione
      if (filters.tipologiaLavorazione && ordine.tipologia_lavorazione !== filters.tipologiaLavorazione) {
        return false;
      }
      
      // Filtro stato fatturazione
      if (filters.statoFatturazione) {
        const importoTotale = ordine.importo_totale || 0;
        const importoFatturato = ordine.importo_fatturato || 0;
        
        if (filters.statoFatturazione === 'completato' && importoFatturato < importoTotale) {
          return false;
        }
        if (filters.statoFatturazione === 'parziale' && (importoFatturato === 0 || importoFatturato >= importoTotale)) {
          return false;
        }
        if (filters.statoFatturazione === 'da_fatturare' && importoFatturato > 0) {
          return false;
        }
      }
      
      // Filtro data arrivo ordine
      if (filters.dataInizio) {
        const dataOrdine = ordine.data_arrivo_ordine ? new Date(ordine.data_arrivo_ordine) : null;
        const dataInizio = new Date(filters.dataInizio);
        if (!dataOrdine || dataOrdine < dataInizio) return false;
      }
      
      if (filters.dataFine) {
        const dataOrdine = ordine.data_arrivo_ordine ? new Date(ordine.data_arrivo_ordine) : null;
        const dataFine = new Date(filters.dataFine);
        if (!dataOrdine || dataOrdine > dataFine) return false;
      }
      
      return true;
    });
  };
  
  // Reset filtri
  const resetFilters = () => {
    setFilters({
      searchText: '',
      clienteId: '',
      pmId: '',
      pmClienteFilter: '',
      statoFatturazione: '',
      tipologiaLavorazione: '',
      dataInizio: '',
      dataFine: ''
    });
  };
  
  // Conteggio filtri attivi
  const activeFiltersCount = Object.values(filters).filter(v => v !== '').length;

  // Export Excel
  const handleExportExcel = async () => {
    setExportLoading(true);
    try {
      const filteredData = getFilteredOrdini();
      
      // Prepara i dati per l'export
      const exportData = filteredData.map(ordine => ({
        'Codice Main Solution': ordine.codice_progetto_main_solution || '',
        'Codice Cliente': ordine.codice_progetto_cliente || '',
        'Cliente': ordine.cliente_nome || '',
        'Progetto': ordine.nome_progetto || '',
        'Tipologia Lavorazione': ordine.tipologia_lavorazione || '',
        'Descrizione Attività': ordine.descrizione_attivita || '',
        'Ref. Main Solution': ordine.pm_main_solution_nome || '',
        'PM Cliente': ordine.pm_cliente || '',
        'Referente Cliente': ordine.referente_cliente || '',
        'Importo Totale (€)': ordine.importo_totale || 0,
        'Importo Fatturato (€)': ordine.importo_fatturato || 0,
        'Importo Da Fatturare (€)': ordine.importo_da_fatturare || 0,
        'Data Arrivo Ordine': ordine.data_arrivo_ordine || '',
        'Data Conferma Ordine': ordine.data_conferma_ordine || '',
        'Data Inizio Lavoro': ordine.data_inizio_lavoro || '',
        'Data Fine Lavoro': ordine.data_fine_lavoro || '',
        'N° Ordine': ordine.numero_ordine || '',
        'N° Progetto': ordine.progetto_numero || '',
      }));
      
      // Chiama l'API per generare l'Excel
      const response = await axios.post(`${API}/ordini-lavoro/export`, 
        { data: exportData },
        { 
          ...getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      // Download del file
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `ordini_lavoro_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Export completato: ${filteredData.length} ordini esportati`);
    } catch (error) {
      console.error('Errore export:', error);
      toast.error('Errore durante l\'export');
    } finally {
      setExportLoading(false);
    }
  };

  // ============================================
  // EXCEL IMPORT FUNCTIONS
  // ============================================
  
  const handleImportFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Verifica estensione
    if (!file.name.endsWith('.xlsx') && !file.name.endsWith('.xls')) {
      toast.error('Seleziona un file Excel (.xlsx o .xls)');
      return;
    }
    
    setImportFile(file);
    setImportLoading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/ordini-lavoro/import/preview`,
        formData,
        {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setImportPreview(response.data);
      setImportStep(2);
      
      // Auto-mapping intelligente basato sui nomi colonne
      const autoMappings = {};
      const availableFields = response.data.available_fields;
      
      response.data.columns.forEach(col => {
        const colNameLower = col.name.toLowerCase();
        
        // Match automatico per nomi comuni
        if (colNameLower.includes('cliente') && !colNameLower.includes('codice') && !colNameLower.includes('referente') && !colNameLower.includes('pm')) {
          autoMappings['cliente_nome'] = col.name;
        } else if (colNameLower.includes('referente') && colNameLower.includes('cliente')) {
          autoMappings['referente_cliente'] = col.name;
        } else if (colNameLower.includes('pm') && colNameLower.includes('cliente')) {
          autoMappings['pm_cliente'] = col.name;
        } else if (colNameLower.includes('codice') && colNameLower.includes('cliente')) {
          autoMappings['codice_progetto_cliente'] = col.name;
        } else if (colNameLower.includes('codice') && (colNameLower.includes('main') || colNameLower.includes('ms'))) {
          autoMappings['codice_progetto_main_solution'] = col.name;
        } else if (colNameLower.includes('progetto') || colNameLower.includes('nome')) {
          if (!autoMappings['nome_progetto']) autoMappings['nome_progetto'] = col.name;
        } else if (colNameLower.includes('descrizione') && !colNameLower.includes('sal')) {
          autoMappings['descrizione_attivita'] = col.name;
        } else if (colNameLower.includes('arrivo') || (colNameLower.includes('data') && colNameLower.includes('ordine'))) {
          if (!autoMappings['data_arrivo_ordine']) autoMappings['data_arrivo_ordine'] = col.name;
        } else if (colNameLower.includes('conferma')) {
          autoMappings['data_conferma_ordine'] = col.name;
        } else if (colNameLower.includes('inizio')) {
          autoMappings['data_inizio_lavoro'] = col.name;
        } else if (colNameLower.includes('fine')) {
          autoMappings['data_fine_lavoro'] = col.name;
        } else if (colNameLower.includes('pm') && (colNameLower.includes('main') || colNameLower.includes('ms') || colNameLower.includes('solution'))) {
          autoMappings['pm_main_solution_nome'] = col.name;
        } else if ((colNameLower.includes('importo') && colNameLower.includes('totale')) || (colNameLower === 'importo totale')) {
          autoMappings['importo_totale'] = col.name;
        }
        
        // Auto-mapping SAL columns
        for (let i = 1; i <= 5; i++) {
          if (colNameLower.includes(`sal ${i}`) || colNameLower.includes(`sal${i}`)) {
            if (colNameLower.includes('%') || colNameLower.includes('percent')) {
              autoMappings[`sal_${i}_percentuale`] = col.name;
            } else if (colNameLower.includes('importo')) {
              autoMappings[`sal_${i}_importo`] = col.name;
            } else if (colNameLower.includes('stato')) {
              autoMappings[`sal_${i}_stato`] = col.name;
            } else if (colNameLower.includes('descr')) {
              autoMappings[`sal_${i}_descrizione`] = col.name;
            }
          }
        }
      });
      
      setImportMappings(autoMappings);
      
    } catch (error) {
      console.error('Errore preview Excel:', error);
      toast.error(error.response?.data?.detail || 'Errore nella lettura del file Excel');
      setImportFile(null);
    } finally {
      setImportLoading(false);
    }
  };
  
  // Prepara le righe editabili dallo step 2 (mapping) allo step 3 (editing)
  const handlePrepareEditableRows = () => {
    if (!importPreview) return;
    
    // Usa ALL_ROWS invece di preview_rows per avere tutte le righe
    const sourceRows = importPreview.all_rows || importPreview.preview_rows;
    
    // Converti le righe Excel in righe editabili con i campi mappati
    const rows = sourceRows.map((row, idx) => {
      // getValue cerca prima nella mappatura, poi prova nomi colonna comuni
      const getValue = (fieldKey) => {
        // Prima prova con la mappatura
        const excelCol = importMappings[fieldKey];
        if (excelCol && row[excelCol] !== undefined && row[excelCol] !== '') {
          return row[excelCol];
        }
        
        // Fallback: cerca direttamente nella riga con nomi colonna standard
        const fallbackNames = {
          'cliente_nome': ['Cliente', 'cliente', 'CLIENTE'],
          'nome_progetto': ['Nome Progetto', 'nome_progetto', 'NOME PROGETTO'],
          'tipologia_lavorazione': ['Tipologia Lavorazione', 'tipologia_lavorazione', 'TIPOLOGIA', 'Tipologia', 'Tipo Lavorazione'],
          'descrizione_attivita': ['Descrizione Attività', 'descrizione_attivita', 'DESCRIZIONE', 'Descrizione'],
          'importo_totale': ['Importo Totale', 'importo_totale', 'IMPORTO TOTALE'],
          'sal_1_percentuale': ['SAL 1 %', 'sal 1 %'],
          'sal_1_importo': ['SAL 1 Importo', 'sal 1 importo'],
          'sal_1_stato': ['SAL 1 Stato', 'sal 1 stato'],
          'sal_2_percentuale': ['SAL 2 %', 'sal 2 %'],
          'sal_2_importo': ['SAL 2 Importo', 'sal 2 importo'],
          'sal_2_stato': ['SAL 2 Stato', 'sal 2 stato'],
          'sal_3_percentuale': ['SAL 3 %', 'sal 3 %'],
          'sal_3_importo': ['SAL 3 Importo', 'sal 3 importo'],
          'sal_3_stato': ['SAL 3 Stato', 'sal 3 stato'],
          'sal_4_percentuale': ['SAL 4 %', 'sal 4 %'],
          'sal_4_importo': ['SAL 4 Importo', 'sal 4 importo'],
          'sal_4_stato': ['SAL 4 Stato', 'sal 4 stato'],
          'sal_5_percentuale': ['SAL 5 %', 'sal 5 %'],
          'sal_5_importo': ['SAL 5 Importo', 'sal 5 importo'],
          'sal_5_stato': ['SAL 5 Stato', 'sal 5 stato'],
        };
        
        const names = fallbackNames[fieldKey] || [];
        for (const name of names) {
          if (row[name] !== undefined && row[name] !== '') {
            return row[name];
          }
        }
        
        return '';
      };
      
      // Estrai SAL dalla mappatura
      const salList = [];
      
      for (let i = 1; i <= 5; i++) {
        const percStr = getValue(`sal_${i}_percentuale`);
        const importoStr = getValue(`sal_${i}_importo`);
        const statoRaw = getValue(`sal_${i}_stato`) || '';
        const descrizione = getValue(`sal_${i}_descrizione`) || '';
        
        // Parse percentuale
        let percentuale = null;
        if (percStr) {
          const cleaned = percStr.toString().replace('%', '').replace(',', '.').trim();
          percentuale = parseFloat(cleaned) || null;
        }
        
        // Parse importo - FIX: gestisce correttamente i decimali
        let importo = null;
        if (importoStr) {
          let cleaned = importoStr.toString().trim();
          // Rimuovi simbolo euro e spazi
          cleaned = cleaned.replace(/[€\s]/g, '');
          
          // Determina il formato (italiano vs inglese)
          // Italiano: 1.234,56 (punto migliaia, virgola decimale)
          // Inglese: 1,234.56 (virgola migliaia, punto decimale)
          
          const hasCommaDecimal = cleaned.match(/,\d{1,2}$/); // Termina con ,XX
          const hasDotDecimal = cleaned.match(/\.\d{1,2}$/);   // Termina con .XX
          
          if (hasCommaDecimal) {
            // Formato italiano: rimuovi punti migliaia, converti virgola in punto
            cleaned = cleaned.replace(/\./g, '').replace(',', '.');
          } else if (hasDotDecimal) {
            // Formato inglese: rimuovi virgole migliaia
            cleaned = cleaned.replace(/,/g, '');
          } else {
            // Nessun decimale chiaro, rimuovi separatori migliaia
            cleaned = cleaned.replace(/[.,]/g, '');
          }
          
          importo = parseFloat(cleaned) || null;
        }
        
        // Parse stato - logica migliorata
        let stato = 'non_fatturabile'; // default
        if (statoRaw) {
          const statoLower = statoRaw.toString().toLowerCase().trim();
          if (statoLower === 'fatturato' || statoLower === 'fatt' || statoLower === 'f') {
            stato = 'fatturato';
          } else if (statoLower.includes('da_fatturare') || statoLower.includes('da fatturare') || statoLower === 'da fatt' || statoLower === 'df') {
            stato = 'da_fatturare';
          } else if (statoLower.includes('non') || statoLower === 'nf' || statoLower === 'non fatt') {
            stato = 'non_fatturabile';
          } else if (statoLower.includes('fattur')) {
            // Se contiene "fattur" ma non "non" e non "da", è fatturato
            stato = 'fatturato';
          }
        }
        
        // Aggiungi SAL solo se ha dati (percentuale, importo, o stato esplicito diverso dal default)
        if (percentuale || importo || (statoRaw && statoRaw.trim() !== '')) {
          salList.push({
            numero_sal: i,
            percentuale: percentuale,
            importo: importo,
            stato: stato,
            descrizione: descrizione
          });
        }
      }
      
      // Cerca cliente_id dal nome
      const clienteNome = getValue('cliente_nome');
      let clienteId = '';
      if (clienteNome) {
        const clienteFound = clienti.find(c => 
          c.nome_azienda.toLowerCase().includes(clienteNome.toLowerCase()) ||
          clienteNome.toLowerCase().includes(c.nome_azienda.toLowerCase())
        );
        if (clienteFound) clienteId = clienteFound.id;
      }
      
      // Cerca pm_id dal nome
      const pmNome = getValue('pm_main_solution_nome');
      let pmId = defaultPmId || '';
      if (pmNome) {
        const pmFound = utenti.find(u => {
          const fullName = `${u.nome} ${u.cognome}`.toLowerCase();
          return fullName.includes(pmNome.toLowerCase()) || pmNome.toLowerCase().includes(fullName);
        });
        if (pmFound) pmId = pmFound.id;
      }
      
      // Parse importo totale - gestisce correttamente i decimali
      let importo = 0;
      const importoStr = getValue('importo_totale');
      if (importoStr) {
        let cleaned = importoStr.toString().trim();
        cleaned = cleaned.replace(/[€\s]/g, '');
        
        const hasCommaDecimal = cleaned.match(/,\d{1,2}$/);
        const hasDotDecimal = cleaned.match(/\.\d{1,2}$/);
        
        if (hasCommaDecimal) {
          cleaned = cleaned.replace(/\./g, '').replace(',', '.');
        } else if (hasDotDecimal) {
          cleaned = cleaned.replace(/,/g, '');
        } else {
          cleaned = cleaned.replace(/[.,]/g, '');
        }
        
        importo = parseFloat(cleaned) || 0;
      }
      
      // Determina stato fatturazione dai SAL
      let statoFatturazione = 'non_fatturabile';
      if (salList.length > 0) {
        // Se almeno un SAL è fatturato, l'ordine è parzialmente/totalmente fatturato
        const hasFatturato = salList.some(s => s.stato === 'fatturato');
        const hasDaFatturare = salList.some(s => s.stato === 'da_fatturare');
        
        if (hasFatturato && !hasDaFatturare && salList.every(s => s.stato === 'fatturato' || s.stato === 'non_fatturabile')) {
          statoFatturazione = 'fatturato';
        } else if (hasFatturato || hasDaFatturare) {
          statoFatturazione = 'da_fatturare';
        }
      }
      
      return {
        id: idx,
        selected: true, // Selezionato per importazione di default
        cliente_id: clienteId,
        cliente_nome: clienteNome,
        referente_cliente: getValue('referente_cliente'),
        pm_cliente: getValue('pm_cliente'),
        codice_progetto_cliente: getValue('codice_progetto_cliente'),
        codice_progetto_main_solution: getValue('codice_progetto_main_solution'),
        nome_progetto: getValue('nome_progetto'),
        tipologia_lavorazione: getValue('tipologia_lavorazione'),
        descrizione_attivita: getValue('descrizione_attivita'),
        data_arrivo_ordine: getValue('data_arrivo_ordine') || new Date().toISOString().split('T')[0],
        data_conferma_ordine: getValue('data_conferma_ordine'),
        data_inizio_lavoro: getValue('data_inizio_lavoro'),
        data_fine_lavoro: getValue('data_fine_lavoro'),
        pm_main_solution_id: pmId,
        importo_totale: importo,
        sal_list: salList.length > 0 ? salList : null,
        stato_fatturazione: statoFatturazione,
        _hasError: !clienteId, // Flag errore se cliente non trovato
        _errorMsg: !clienteId ? 'Seleziona un cliente' : '',
        _salCount: salList.length
      };
    });
    
    setEditableRows(rows);
    setEditPage(1); // Reset pagina
    setImportStep(3);
  };
  
  // Aggiorna una riga editabile
  const handleUpdateEditableRow = (rowId, field, value) => {
    setEditableRows(prev => prev.map(row => {
      if (row.id !== rowId) return row;
      
      const updated = { ...row, [field]: value };
      
      // Se cambia il cliente_id, aggiorna anche il nome
      if (field === 'cliente_id') {
        const cliente = clienti.find(c => c.id === value);
        updated.cliente_nome = cliente ? cliente.nome_azienda : '';
        updated._hasError = !value;
        updated._errorMsg = !value ? 'Seleziona un cliente' : '';
      }
      
      return updated;
    }));
  };
  
  // Aggiorna un SAL specifico di una riga
  const handleUpdateSAL = (rowId, salIndex, field, value) => {
    setEditableRows(prev => prev.map(row => {
      if (row.id !== rowId) return row;
      
      // Assicurati che sal_list esista
      let salList = row.sal_list ? [...row.sal_list] : [];
      
      // Crea il SAL se non esiste
      while (salList.length <= salIndex) {
        salList.push({
          numero_sal: salList.length + 1,
          percentuale: null,
          importo: null,
          stato: 'non_fatturabile',
          descrizione: ''
        });
      }
      
      // Aggiorna il campo
      salList[salIndex] = { ...salList[salIndex], [field]: value };
      
      // Ricalcola importo se cambia percentuale
      if (field === 'percentuale' && row.importo_totale) {
        const perc = parseFloat(value) || 0;
        salList[salIndex].importo = (perc / 100) * row.importo_totale;
      }
      
      // Aggiorna conteggio SAL
      const salCount = salList.filter(s => s.percentuale || s.importo).length;
      
      return { ...row, sal_list: salList, _salCount: salCount };
    }));
  };
  
  // Toggle selezione riga
  const handleToggleRowSelection = (rowId) => {
    setEditableRows(prev => prev.map(row => 
      row.id === rowId ? { ...row, selected: !row.selected } : row
    ));
  };
  
  // Esegue l'importazione finale delle righe selezionate
  const handleExecuteImport = async () => {
    const selectedRows = editableRows.filter(r => r.selected && r.cliente_id);
    
    if (selectedRows.length === 0) {
      toast.error('Seleziona almeno una riga con cliente valido');
      return;
    }
    
    setImportLoading(true);
    
    try {
      const response = await axios.post(
        `${API}/ordini-lavoro/import/execute-rows`,
        { rows: selectedRows },
        getAuthHeaders()
      );
      
      setImportResult(response.data);
      setImportStep(4);
      
      if (response.data.imported > 0) {
        toast.success(`${response.data.imported} ordini importati con successo!`);
        fetchOrdini();
      }
      
    } catch (error) {
      console.error('Errore importazione:', error);
      toast.error(error.response?.data?.detail || 'Errore nell\'importazione');
    } finally {
      setImportLoading(false);
    }
  };
  
  const resetImport = () => {
    setImportStep(1);
    setImportFile(null);
    setImportPreview(null);
    setImportMappings({});
    setImportResult(null);
    setDefaultPmId('');
    setEditableRows([]);
    setEditPage(1);
    setSalEditRowId(null);
  };
  
  const handleImportDialogClose = (open) => {
    if (!open) {
      resetImport();
    }
    setImportDialogOpen(open);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-3 py-3 sm:px-6 sm:py-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="ghost"
                onClick={() => navigate('/')}
                data-testid="back-to-dashboard"
                className="mr-2"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div className="hidden sm:block bg-green-600 p-3 rounded-xl shadow-md">
                <Briefcase className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-xl sm:text-3xl font-bold text-slate-900">Ordini di Lavoro</h1>
                <p className="text-sm text-slate-600">PaMa - Benvenuto, {user.nome}</p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2">
              {/* Pulsante Importa Excel */}
              <Button 
                onClick={() => setImportDialogOpen(true)}
                data-testid="import-excel-btn"
                variant="outline"
                className="border-green-600 text-green-600 hover:bg-green-50"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Importa Excel
              </Button>
              
              <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
                <DialogTrigger asChild>
                  <Button 
                    data-testid="add-ordine-btn"
                    className="bg-green-600 hover:bg-green-700 text-white shadow-md"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nuovo Ordine
                  </Button>
                </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>
                    {editingOrdine ? 'Modifica Ordine' : 'Nuovo Ordine di Lavoro'}
                  </DialogTitle>
                </DialogHeader>
                
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Preventivo Collegato - Prima di tutto */}
                  {!editingOrdine && (
                    <div className="p-4 bg-blue-50 border-2 border-blue-200 rounded-lg">
                      <Label htmlFor="preventivo_id" className="text-blue-900 font-semibold">🔗 Preventivo Collegato (opzionale)</Label>
                      <p className="text-xs text-blue-700 mb-2">Seleziona un preventivo accettato per compilare automaticamente il cliente</p>
                      <select
                        id="preventivo_id"
                        name="preventivo_id"
                        value={formData.preventivo_id}
                        onChange={(e) => {
                          const preventivoId = e.target.value;
                          if (preventivoId) {
                            const preventivo = preventivi.find(p => p.id === preventivoId);
                            if (preventivo) {
                              setFormData(prev => ({
                                ...prev,
                                preventivo_id: preventivoId,
                                cliente_id: preventivo.cliente_id
                              }));
                              toast.success(`Cliente "${preventivo.cliente_nome}" selezionato automaticamente dal preventivo`);
                            }
                          } else {
                            setFormData(prev => ({
                              ...prev,
                              preventivo_id: ''
                            }));
                          }
                        }}
                        className="mt-1 flex h-10 w-full rounded-md border border-blue-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 font-medium"
                      >
                        <option value="">-- Nessun preventivo --</option>
                        {preventivi.length === 0 ? (
                          <option value="" disabled>Nessun preventivo accettato disponibile</option>
                        ) : (
                          preventivi.map(prev => (
                            <option key={prev.id} value={prev.id}>
                              {prev.numero_preventivo} - {prev.cliente_nome} - €{(prev.prezzo_manuale || 0).toLocaleString('it-IT', {minimumFractionDigits: 2, maximumFractionDigits: 2})}
                            </option>
                          ))
                        )}
                      </select>
                      {preventivi.length === 0 && (
                        <p className="text-xs text-blue-600 mt-1">
                          ℹ️ Solo i preventivi con stato &quot;accettato&quot; appaiono qui. Vai in Preventivi per cambiare lo stato.
                        </p>
                      )}
                    </div>
                  )}

                  {/* Cliente */}
                  <div>
                    <Label>Cliente *</Label>
                    <Select 
                      value={formData.cliente_id} 
                      onValueChange={(value) => setFormData({...formData, cliente_id: value})}
                      required
                    >
                      <SelectTrigger className="mt-1" data-testid="select-cliente">
                        <SelectValue placeholder="Seleziona cliente" />
                      </SelectTrigger>
                      <SelectContent>
                        {clienti.map(cliente => (
                          <SelectItem key={cliente.id} value={cliente.id}>
                            {cliente.numero_ms} - {cliente.nome_azienda}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Referenti */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="referente_cliente">Referente Cliente *</Label>
                      <Input
                        id="referente_cliente"
                        name="referente_cliente"
                        value={formData.referente_cliente}
                        onChange={handleInputChange}
                        placeholder="Nome referente del cliente"
                        required
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="pm_cliente">PM del Cliente *</Label>
                      <Input
                        id="pm_cliente"
                        name="pm_cliente"
                        value={formData.pm_cliente}
                        onChange={handleInputChange}
                        placeholder="Project Manager del cliente"
                        required
                        className="mt-1"
                      />
                    </div>
                  </div>

                  {/* Codici Progetto */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="codice_progetto_cliente">
                        Codice Progetto Cliente {!formData.multiple_commesse && '*'}
                        {formData.multiple_commesse && (
                          <span className="text-xs text-purple-600 ml-2">(opzionale per più commesse)</span>
                        )}
                      </Label>
                      <Input
                        id="codice_progetto_cliente"
                        name="codice_progetto_cliente"
                        value={formData.codice_progetto_cliente}
                        onChange={handleInputChange}
                        required={!formData.multiple_commesse}
                        className="mt-1"
                        placeholder={formData.multiple_commesse ? "Opzionale - più codici su progetti separati" : ""}
                      />
                    </div>
                    <div>
                      <Label htmlFor="protocollo_ordine_lavoro">Protocollo Ordine di Lavoro</Label>
                      <Input
                        id="protocollo_ordine_lavoro"
                        name="protocollo_ordine_lavoro"
                        value={formData.protocollo_ordine_lavoro}
                        onChange={handleInputChange}
                        className="mt-1"
                        placeholder="Es. PROT-2026-001"
                      />
                    </div>
                  </div>

                  {/* Codice Main Solution */}
                  <div>
                    <Label htmlFor="codice_progetto_main_solution">Codice Progetto Main Solution</Label>
                    <Input
                      id="codice_progetto_main_solution"
                      name="codice_progetto_main_solution"
                      value={formData.codice_progetto_main_solution}
                      onChange={handleInputChange}
                      className="mt-1"
                      placeholder="Opzionale"
                    />
                  </div>

                  {/* Nome Progetto */}
                  <div>
                    <Label htmlFor="nome_progetto">Nome Progetto *</Label>
                    <Input
                      id="nome_progetto"
                      name="nome_progetto"
                      value={formData.nome_progetto}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                    />
                  </div>

                  {/* Tipologia Lavorazione e Descrizione Attività - sulla stessa riga */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="tipologia_lavorazione">Tipologia Lavorazione</Label>
                      <Select
                        value={formData.tipologia_lavorazione}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, tipologia_lavorazione: value }))}
                      >
                        <SelectTrigger className="mt-1" data-testid="tipologia-lavorazione-select">
                          <SelectValue placeholder="Seleziona tipologia..." />
                        </SelectTrigger>
                        <SelectContent>
                          {TIPOLOGIE_LAVORAZIONE.map(tipo => (
                            <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="descrizione_attivita">Descrizione Attività *</Label>
                      <Input
                        id="descrizione_attivita"
                        name="descrizione_attivita"
                        value={formData.descrizione_attivita}
                        onChange={handleInputChange}
                        required
                        className="mt-1"
                      />
                    </div>
                  </div>

                  {/* Date Ordine */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="data_arrivo_ordine">Data Arrivo Ordine *</Label>
                      <Input
                        id="data_arrivo_ordine"
                        name="data_arrivo_ordine"
                        type="date"
                        value={formData.data_arrivo_ordine}
                        onChange={handleInputChange}
                        required
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="data_conferma_ordine">Data Conferma Ordine</Label>
                      <Input
                        id="data_conferma_ordine"
                        name="data_conferma_ordine"
                        type="date"
                        value={formData.data_conferma_ordine}
                        onChange={handleInputChange}
                        className="mt-1"
                      />
                    </div>
                  </div>

                  {/* Date Lavoro */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="data_inizio_lavoro">Data Inizio Lavoro</Label>
                      <Input
                        id="data_inizio_lavoro"
                        name="data_inizio_lavoro"
                        type="date"
                        value={formData.data_inizio_lavoro}
                        onChange={handleInputChange}
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label htmlFor="data_fine_lavoro">Data Fine Lavoro</Label>
                      <Input
                        id="data_fine_lavoro"
                        name="data_fine_lavoro"
                        type="date"
                        value={formData.data_fine_lavoro}
                        onChange={handleInputChange}
                        className="mt-1"
                      />
                    </div>
                  </div>

                  {/* PM Main Solution */}
                  <div>
                    <Label>Referente Main Solution (PM) *</Label>
                    <Select 
                      value={formData.pm_main_solution_id} 
                      onValueChange={(value) => setFormData({...formData, pm_main_solution_id: value})}
                      required
                    >
                      <SelectTrigger className="mt-1" data-testid="select-pm">
                        <SelectValue placeholder="Seleziona referente Main Solution" />
                      </SelectTrigger>
                      <SelectContent>
                        {utenti.map(utente => (
                          <SelectItem key={utente.id} value={utente.id}>
                            {utente.nome} {utente.cognome} - {utente.ruolo}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Importo Totale */}
                  <div>
                    <Label htmlFor="importo_totale">Importo Totale Ordine * (€)</Label>
                    <Input
                      id="importo_totale"
                      name="importo_totale"
                      type="number"
                      step="0.01"
                      value={formData.importo_totale}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                    />
                  </div>

                  {/* SAL */}
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <Label>Stati Avanzamento Lavori (SAL)</Label>
                      <Button 
                        type="button" 
                        onClick={handleAddSAL}
                        variant="outline"
                        size="sm"
                        data-testid="add-sal-btn"
                      >
                        <Plus className="w-4 h-4 mr-1" />
                        Aggiungi SAL
                      </Button>
                    </div>

                    {formData.sal_list.map((sal, index) => (
                      <div key={index} className="p-4 bg-slate-50 rounded-lg space-y-3 border border-slate-200">
                        <div className="flex items-center justify-between">
                          <Label className="font-semibold text-slate-800">SAL {sal.numero_sal}</Label>
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleRemoveSAL(index)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Elimina
                          </Button>
                        </div>
                        
                        {/* Descrizione SAL */}
                        <div>
                          <Label className="text-xs">Descrizione SAL</Label>
                          <div className="flex gap-2 mt-1">
                            <Input
                              type="text"
                              placeholder="Scrivi o seleziona una descrizione..."
                              value={sal.descrizione}
                              onChange={(e) => handleSALChange(index, 'descrizione', e.target.value)}
                              className="flex-1"
                              list={`descrizioni-sal-${index}`}
                            />
                            <datalist id={`descrizioni-sal-${index}`}>
                              {descrizioniSAL.map((desc, i) => (
                                <option key={i} value={desc} />
                              ))}
                            </datalist>
                            <Select 
                              key={`select-sal-${index}-${descrizioniSAL.length}`}
                              onValueChange={(value) => handleSALChange(index, 'descrizione', value)}
                            >
                              <SelectTrigger className="w-[120px]">
                                <SelectValue placeholder="Seleziona" />
                              </SelectTrigger>
                              <SelectContent>
                                {descrizioniSAL.map((desc, i) => (
                                  <SelectItem key={i} value={desc}>{desc}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setDialogDescrizioneSAL({ open: true, salIndex: index });
                                setInputNuovaDescrizioneSAL('');
                              }}
                              className="whitespace-nowrap"
                            >
                              + Aggiungi
                            </Button>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                          <div>
                            <Label className="text-xs">Percentuale % *</Label>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              max="100"
                              placeholder="es: 30"
                              value={sal.percentuale}
                              onChange={(e) => handleSALChange(index, 'percentuale', e.target.value)}
                              className="mt-1"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Importo Calcolato (€)</Label>
                            <Input
                              type="text"
                              value={sal.importo ? sal.importo.toFixed(2) : '0.00'}
                              disabled
                              className="mt-1 bg-slate-100"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Stato Fatturazione *</Label>
                            <Select 
                              value={sal.stato} 
                              onValueChange={(value) => handleSALChange(index, 'stato', value)}
                            >
                              <SelectTrigger className="mt-1">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="non_fatturabile">Non Fatturabile</SelectItem>
                                <SelectItem value="da_fatturare">Da Fatturare</SelectItem>
                                <SelectItem value="fatturato">Fatturato</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          {/* Campo OdA/Riferimento Fattura - visibile solo se stato != non_fatturabile */}
                          {(sal.stato === 'da_fatturare' || sal.stato === 'fatturato') && (
                            <div className="col-span-full">
                              <Label className="text-xs text-orange-700">OdA o Riferimento da inserire in fattura</Label>
                              <Input
                                type="text"
                                value={sal.riferimento_fattura || ''}
                                onChange={(e) => handleSALChange(index, 'riferimento_fattura', e.target.value)}
                                className="mt-1 border-orange-300 focus:border-orange-500"
                                placeholder="Es: OdA 12345, Rif. Contratto ABC..."
                              />
                            </div>
                          )}
                        </div>
                        
                        {/* Termini di Pagamento */}
                        <div className="border-t pt-3 mt-3">
                          <Label className="text-sm font-medium text-slate-700 mb-2 block">Termini di Pagamento</Label>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-xs">Giorni</Label>
                              <div className="flex gap-2 mt-1">
                                <Select 
                                  value={[15, 30, 45, 60].includes(sal.termini_pagamento_giorni) ? String(sal.termini_pagamento_giorni) : 'custom'} 
                                  onValueChange={(value) => {
                                    if (value !== 'custom') {
                                      handleSALChange(index, 'termini_pagamento_giorni', parseInt(value));
                                    }
                                  }}
                                >
                                  <SelectTrigger className="w-[100px]">
                                    <SelectValue />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="15">15 gg</SelectItem>
                                    <SelectItem value="30">30 gg</SelectItem>
                                    <SelectItem value="45">45 gg</SelectItem>
                                    <SelectItem value="60">60 gg</SelectItem>
                                    <SelectItem value="custom">Altro...</SelectItem>
                                  </SelectContent>
                                </Select>
                                {![15, 30, 45, 60].includes(sal.termini_pagamento_giorni) && (
                                  <Input
                                    type="number"
                                    min="1"
                                    max="365"
                                    value={sal.termini_pagamento_giorni || ''}
                                    onChange={(e) => handleSALChange(index, 'termini_pagamento_giorni', parseInt(e.target.value) || 30)}
                                    className="w-[80px]"
                                    placeholder="gg"
                                  />
                                )}
                              </div>
                            </div>
                            <div className="flex items-end">
                              <label className="flex items-center gap-2 cursor-pointer p-2 rounded hover:bg-slate-100">
                                <input
                                  type="checkbox"
                                  checked={sal.fattura_fine_mese || false}
                                  onChange={(e) => handleSALChange(index, 'fattura_fine_mese', e.target.checked)}
                                  className="w-4 h-4 rounded border-slate-300"
                                />
                                <span className="text-sm text-slate-700">Fattura Fine Mese</span>
                              </label>
                            </div>
                          </div>
                          {sal.fattura_fine_mese && (
                            <p className="text-xs text-blue-600 mt-1">
                              Il conteggio dei {sal.termini_pagamento_giorni || 30} giorni partirà dalla fine del mese
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Sezione Allegati - Solo per nuovo ordine */}
                  {!editingOrdine && (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                      <Label className="text-amber-900 font-semibold flex items-center gap-2 mb-3">
                        <Paperclip className="w-4 h-4" />
                        Allegati Ordine
                      </Label>
                      
                      {/* File selezionati */}
                      {allegatiNuovoOrdine.length > 0 && (
                        <div className="mb-3 space-y-2">
                          {allegatiNuovoOrdine.map((file, index) => (
                            <div key={index} className="flex items-center justify-between p-2 bg-white rounded border border-amber-200">
                              <div className="flex items-center gap-2">
                                <FileText className="w-4 h-4 text-amber-600" />
                                <span className="text-sm text-slate-700">{file.name}</span>
                                <span className="text-xs text-slate-500">({(file.size / 1024).toFixed(1)} KB)</span>
                              </div>
                              <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => setAllegatiNuovoOrdine(prev => prev.filter((_, i) => i !== index))}
                                className="text-red-600 hover:bg-red-50"
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {/* Input file */}
                      <div className="flex items-center gap-2">
                        <input
                          type="file"
                          id="allegati_nuovo_ordine"
                          multiple
                          onChange={(e) => {
                            const files = Array.from(e.target.files);
                            setAllegatiNuovoOrdine(prev => [...prev, ...files]);
                            e.target.value = ''; // Reset per permettere selezione stesso file
                          }}
                          className="hidden"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => document.getElementById('allegati_nuovo_ordine').click()}
                          className="border-amber-300 text-amber-700 hover:bg-amber-100"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          Aggiungi Allegato
                        </Button>
                        <span className="text-xs text-amber-600">
                          {allegatiNuovoOrdine.length > 0 
                            ? `${allegatiNuovoOrdine.length} file selezionat${allegatiNuovoOrdine.length === 1 ? 'o' : 'i'}`
                            : 'PDF, Excel, Word, immagini...'}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* Checkbox Più Commesse */}
                  <div className="flex items-center gap-2 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                    <input
                      type="checkbox"
                      id="multiple_commesse"
                      checked={formData.multiple_commesse}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        multiple_commesse: e.target.checked
                      }))}
                      className="w-4 h-4 text-purple-600 border-purple-300 rounded focus:ring-purple-500"
                    />
                    <label htmlFor="multiple_commesse" className="text-sm font-medium text-purple-900 cursor-pointer">
                      📋 Questo ordine ha più commesse (può essere utilizzato su più progetti)
                    </label>
                  </div>

                  {/* Checkbox Crea Progetto Automaticamente */}
                  {!editingOrdine && (
                    <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg">
                      <input
                        type="checkbox"
                        id="crea_progetto_automaticamente"
                        checked={formData.crea_progetto_automaticamente}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          crea_progetto_automaticamente: e.target.checked
                        }))}
                        className="w-4 h-4 text-green-600 border-green-300 rounded focus:ring-green-500"
                      />
                      <label htmlFor="crea_progetto_automaticamente" className="text-sm font-medium text-green-900 cursor-pointer">
                        ✅ Crea progetto automaticamente nella sezione Progetti
                      </label>
                    </div>
                  )}

                  {/* Checkbox Crea Cantiere Automaticamente */}
                  {!editingOrdine && (
                    <div className="flex items-center gap-2 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                      <input
                        type="checkbox"
                        id="crea_cantiere_automaticamente"
                        checked={formData.crea_cantiere_automaticamente}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          crea_cantiere_automaticamente: e.target.checked
                        }))}
                        className="w-4 h-4 text-blue-600 border-blue-300 rounded focus:ring-blue-500"
                      />
                      <label htmlFor="crea_cantiere_automaticamente" className="text-sm font-medium text-blue-900 cursor-pointer">
                        🏗️ Crea cantiere automaticamente nella sezione Cantieri
                      </label>
                    </div>
                  )}

                  <div className="flex justify-between items-center pt-4">
                    <div>
                      {editingOrdine && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            handleViewDetails(editingOrdine);
                          }}
                          className="border-blue-300 text-blue-600 hover:bg-blue-50"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          Visualizza Dettagli e Allegati
                        </Button>
                      )}
                    </div>
                    <div className="flex gap-3">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => handleDialogOpenChange(false)}
                      >
                        Annulla
                      </Button>
                      <Button 
                        type="submit"
                        data-testid="submit-ordine-btn"
                        className="bg-green-600 hover:bg-green-700"
                      >
                        {editingOrdine ? 'Aggiorna' : 'Salva'}
                      </Button>
                    </div>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
            </div>
          </div>
        </div>
      </div>

      {/* Dialog Importazione Excel */}
      <Dialog open={importDialogOpen} onOpenChange={handleImportDialogClose}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileSpreadsheet className="w-6 h-6 text-green-600" />
              Importa Ordini da Excel
            </DialogTitle>
          </DialogHeader>
          
          {/* Step Indicator */}
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${importStep >= 1 ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
              <span className="w-5 h-5 rounded-full bg-green-600 text-white flex items-center justify-center text-xs">1</span>
              Carica
            </div>
            <ArrowRight className="w-4 h-4 text-slate-400" />
            <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${importStep >= 2 ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${importStep >= 2 ? 'bg-green-600 text-white' : 'bg-slate-300 text-white'}`}>2</span>
              Mappa
            </div>
            <ArrowRight className="w-4 h-4 text-slate-400" />
            <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${importStep >= 3 ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${importStep >= 3 ? 'bg-green-600 text-white' : 'bg-slate-300 text-white'}`}>3</span>
              Modifica
            </div>
            <ArrowRight className="w-4 h-4 text-slate-400" />
            <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${importStep >= 4 ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
              <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${importStep >= 4 ? 'bg-green-600 text-white' : 'bg-slate-300 text-white'}`}>4</span>
              Risultato
            </div>
          </div>
          
          {/* Step 1: Upload File */}
          {importStep === 1 && (
            <div className="space-y-4">
              {/* Template Download Section */}
              <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-green-100 rounded-lg">
                    <Download className="w-8 h-8 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-green-800 mb-1">
                      Scarica il Template
                    </h3>
                    <p className="text-sm text-green-700 mb-3">
                      Usa il nostro template Excel con tutte le colonne già pronte. Compilalo e ricaricalo qui.
                    </p>
                    <Button
                      variant="outline"
                      className="border-green-600 text-green-700 hover:bg-green-100"
                      onClick={() => {
                        window.open(`${API}/ordini-lavoro/import/template`, '_blank');
                      }}
                      data-testid="download-template-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Scarica Template Excel
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Divider */}
              <div className="flex items-center gap-4">
                <div className="flex-1 h-px bg-slate-200"></div>
                <span className="text-sm text-slate-500 font-medium">oppure carica il tuo file</span>
                <div className="flex-1 h-px bg-slate-200"></div>
              </div>
              
              {/* Upload Section */}
              <div className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center hover:border-green-500 transition-colors">
                <FileSpreadsheet className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                <h3 className="text-base font-semibold text-slate-700 mb-2">
                  Carica un file Excel
                </h3>
                <p className="text-sm text-slate-500 mb-4">
                  Supportati: .xlsx, .xls - Potrai modificare i dati prima di importare
                </p>
                <label className="cursor-pointer">
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    className="hidden"
                    onChange={handleImportFileSelect}
                    disabled={importLoading}
                  />
                  <Button
                    type="button"
                    disabled={importLoading}
                    className="bg-green-600 hover:bg-green-700"
                    onClick={(e) => e.currentTarget.previousElementSibling.click()}
                  >
                    {importLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analisi in corso...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4 mr-2" />
                        Seleziona File
                      </>
                    )}
                  </Button>
                </label>
              </div>
            </div>
          )}
          
          {/* Step 2: Mapping */}
          {importStep === 2 && importPreview && (
            <div className="space-y-4">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-sm text-green-800">
                  <Check className="w-4 h-4 inline mr-1" />
                  File caricato: <strong>{importFile?.name}</strong> - {importPreview.total_rows} righe trovate
                </p>
              </div>
              
              {/* PM Default */}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-4">
                <Label className="font-semibold text-slate-700">PM Main Solution di Default</Label>
                <p className="text-xs text-slate-500 mb-2">
                  Verrà assegnato a tutte le righe senza PM specificato
                </p>
                <Select value={defaultPmId} onValueChange={setDefaultPmId}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Seleziona PM di default..." />
                  </SelectTrigger>
                  <SelectContent>
                    {utenti.filter(u => u.ruolo === 'admin' || u.ruolo === 'master' || userHasAmbito(u, 'ingegnere')).map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.nome} {u.cognome} ({u.ruolo})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Mapping Fields - Compact */}
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="bg-slate-100 px-4 py-2 border-b border-slate-200">
                  <h4 className="font-semibold text-slate-700">Mappatura Campi</h4>
                </div>
                <div className="p-4 grid grid-cols-2 gap-3 max-h-48 overflow-y-auto">
                  {importPreview.available_fields.map(field => (
                    <div key={field.key} className="flex items-center gap-2">
                      <span className={`text-xs w-32 truncate ${field.required ? 'font-semibold' : ''}`}>
                        {field.label}{field.required && <span className="text-red-500">*</span>}
                      </span>
                      <Select
                        value={importMappings[field.key] || ''}
                        onValueChange={(value) => setImportMappings(prev => ({
                          ...prev,
                          [field.key]: value === '_none_' ? '' : value
                        }))}
                      >
                        <SelectTrigger className="flex-1 h-8 text-xs">
                          <SelectValue placeholder="--" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="_none_">--</SelectItem>
                          {importPreview.columns.map(col => (
                            <SelectItem key={col.name} value={col.name}>
                              {col.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex justify-between pt-4 border-t border-slate-200">
                <Button variant="outline" onClick={() => setImportStep(1)}>
                  Indietro
                </Button>
                <Button
                  onClick={handlePrepareEditableRows}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <ArrowRight className="w-4 h-4 mr-2" />
                  Avanti: Modifica Dati
                </Button>
              </div>
            </div>
          )}
          
          {/* Step 3: Edit Data */}
          {importStep === 3 && editableRows.length > 0 && (
            <div className="space-y-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-center justify-between">
                <p className="text-sm text-blue-800">
                  <Edit className="w-4 h-4 inline mr-1" />
                  Modifica i dati prima di importare. <strong>{editableRows.length}</strong> righe totali.
                </p>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-blue-700">Seleziona tutte:</span>
                  <input 
                    type="checkbox"
                    checked={editableRows.every(r => r.selected)}
                    onChange={(e) => setEditableRows(prev => prev.map(r => ({...r, selected: e.target.checked})))}
                    className="w-4 h-4"
                  />
                </div>
              </div>
              
              {/* Pagination Controls Top */}
              <div className="flex items-center justify-between bg-slate-50 p-2 rounded-lg">
                <div className="text-sm text-slate-600">
                  Pagina {editPage} di {Math.ceil(editableRows.length / ROWS_PER_PAGE)} 
                  <span className="ml-2 text-slate-400">
                    (righe {((editPage - 1) * ROWS_PER_PAGE) + 1} - {Math.min(editPage * ROWS_PER_PAGE, editableRows.length)})
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditPage(1)}
                    disabled={editPage === 1}
                  >
                    Prima
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditPage(p => Math.max(1, p - 1))}
                    disabled={editPage === 1}
                  >
                    ← Prec
                  </Button>
                  <span className="px-3 py-1 bg-white border rounded text-sm font-medium">
                    {editPage}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditPage(p => Math.min(Math.ceil(editableRows.length / ROWS_PER_PAGE), p + 1))}
                    disabled={editPage >= Math.ceil(editableRows.length / ROWS_PER_PAGE)}
                  >
                    Succ →
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setEditPage(Math.ceil(editableRows.length / ROWS_PER_PAGE))}
                    disabled={editPage >= Math.ceil(editableRows.length / ROWS_PER_PAGE)}
                  >
                    Ultima
                  </Button>
                </div>
              </div>
              
              {/* Editable Table */}
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-100 sticky top-0">
                      <tr>
                        <th className="px-2 py-2 text-left border-b w-10">#</th>
                        <th className="px-2 py-2 text-left border-b font-semibold text-slate-700 min-w-40">Cliente *</th>
                        <th className="px-2 py-2 text-left border-b font-semibold text-slate-700 min-w-32">Nome Progetto</th>
                        <th className="px-2 py-2 text-left border-b font-semibold text-slate-700 min-w-32">Tipologia</th>
                        <th className="px-2 py-2 text-left border-b font-semibold text-slate-700 min-w-32">Descrizione</th>
                        <th className="px-2 py-2 text-left border-b font-semibold text-slate-700 min-w-24">Referente</th>
                        <th className="px-2 py-2 text-left border-b font-semibold text-slate-700 min-w-24">PM Cliente</th>
                        <th className="px-2 py-2 text-left border-b font-semibold text-slate-700 min-w-24">Cod. Cliente</th>
                        <th className="px-2 py-2 text-left border-b font-semibold text-slate-700 min-w-24">Cod. MS</th>
                        <th className="px-2 py-2 text-left border-b font-semibold text-slate-700 min-w-28">Data Arrivo</th>
                        <th className="px-2 py-2 text-left border-b font-semibold text-slate-700 min-w-24">Importo</th>
                        <th className="px-2 py-2 text-left border-b font-semibold text-slate-700 min-w-32">PM MS</th>
                        <th className="px-2 py-2 text-left border-b font-semibold text-blue-700 min-w-20 bg-blue-50">SAL</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editableRows
                        .slice((editPage - 1) * ROWS_PER_PAGE, editPage * ROWS_PER_PAGE)
                        .map((row) => (
                        <tr key={row.id} className={`hover:bg-slate-50 ${row._hasError ? 'bg-red-50' : ''} ${!row.selected ? 'opacity-50' : ''}`}>
                          <td className="px-2 py-1 border-b">
                            <input 
                              type="checkbox"
                              checked={row.selected}
                              onChange={() => handleToggleRowSelection(row.id)}
                              className="w-4 h-4"
                            />
                          </td>
                          <td className="px-2 py-1 border-b">
                            <Select
                              value={row.cliente_id}
                              onValueChange={(value) => handleUpdateEditableRow(row.id, 'cliente_id', value)}
                            >
                              <SelectTrigger className={`h-8 text-xs ${!row.cliente_id ? 'border-red-300' : ''}`}>
                                <SelectValue placeholder="Seleziona..." />
                              </SelectTrigger>
                              <SelectContent>
                                {clienti.map(c => (
                                  <SelectItem key={c.id} value={c.id}>
                                    {c.nome_azienda}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-2 py-1 border-b">
                            <Input
                              value={row.nome_progetto}
                              onChange={(e) => handleUpdateEditableRow(row.id, 'nome_progetto', e.target.value)}
                              className="h-8 text-xs"
                              placeholder="Nome progetto"
                            />
                          </td>
                          <td className="px-2 py-1 border-b">
                            <Select
                              value={row.tipologia_lavorazione || ''}
                              onValueChange={(value) => handleUpdateEditableRow(row.id, 'tipologia_lavorazione', value)}
                            >
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue placeholder="Seleziona..." />
                              </SelectTrigger>
                              <SelectContent>
                                {TIPOLOGIE_LAVORAZIONE.map(tipo => (
                                  <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-2 py-1 border-b">
                            <Input
                              value={row.descrizione_attivita || ''}
                              onChange={(e) => handleUpdateEditableRow(row.id, 'descrizione_attivita', e.target.value)}
                              className="h-8 text-xs"
                              placeholder="Descrizione"
                            />
                          </td>
                          <td className="px-2 py-1 border-b">
                            <Input
                              value={row.referente_cliente || ''}
                              onChange={(e) => handleUpdateEditableRow(row.id, 'referente_cliente', e.target.value)}
                              className="h-8 text-xs"
                              placeholder="Referente"
                            />
                          </td>
                          <td className="px-2 py-1 border-b">
                            <Input
                              value={row.pm_cliente || ''}
                              onChange={(e) => handleUpdateEditableRow(row.id, 'pm_cliente', e.target.value)}
                              className="h-8 text-xs"
                              placeholder="PM Cliente"
                            />
                          </td>
                          <td className="px-2 py-1 border-b">
                            <Input
                              value={row.codice_progetto_cliente || ''}
                              onChange={(e) => handleUpdateEditableRow(row.id, 'codice_progetto_cliente', e.target.value)}
                              className="h-8 text-xs"
                              placeholder="Cod."
                            />
                          </td>
                          <td className="px-2 py-1 border-b">
                            <Input
                              value={row.codice_progetto_main_solution || ''}
                              onChange={(e) => handleUpdateEditableRow(row.id, 'codice_progetto_main_solution', e.target.value)}
                              className="h-8 text-xs"
                              placeholder="Cod. MS"
                            />
                          </td>
                          <td className="px-2 py-1 border-b">
                            <Input
                              type="date"
                              value={row.data_arrivo_ordine}
                              onChange={(e) => handleUpdateEditableRow(row.id, 'data_arrivo_ordine', e.target.value)}
                              className="h-8 text-xs"
                            />
                          </td>
                          <td className="px-2 py-1 border-b">
                            <Input
                              type="number"
                              value={row.importo_totale}
                              onChange={(e) => handleUpdateEditableRow(row.id, 'importo_totale', parseFloat(e.target.value) || 0)}
                              className="h-8 text-xs w-24"
                              placeholder="0"
                            />
                          </td>
                          <td className="px-2 py-1 border-b">
                            <Select
                              value={row.pm_main_solution_id}
                              onValueChange={(value) => handleUpdateEditableRow(row.id, 'pm_main_solution_id', value)}
                            >
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue placeholder="PM..." />
                              </SelectTrigger>
                              <SelectContent>
                                {utenti.filter(u => u.ruolo === 'admin' || u.ruolo === 'master' || userHasAmbito(u, 'ingegnere')).map(u => (
                                  <SelectItem key={u.id} value={u.id}>
                                    {u.nome} {u.cognome}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </td>
                          <td className="px-2 py-1 border-b bg-blue-50 text-center">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-xs text-blue-700 hover:bg-blue-100"
                              onClick={() => setSalEditRowId(salEditRowId === row.id ? null : row.id)}
                            >
                              {row._salCount > 0 ? `${row._salCount} SAL` : '+ SAL'}
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
              
              {/* SAL Edit Panel - Appare sotto la tabella quando si clicca su SAL */}
              {salEditRowId !== null && (
                <div className="border-2 border-blue-300 rounded-lg p-4 bg-blue-50">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-semibold text-blue-800">
                      Modifica SAL - Riga {salEditRowId + 1}: {editableRows.find(r => r.id === salEditRowId)?.nome_progetto || ''}
                    </h4>
                    <Button variant="ghost" size="sm" onClick={() => setSalEditRowId(null)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
                    {[1, 2, 3, 4, 5].map(salNum => {
                      const row = editableRows.find(r => r.id === salEditRowId);
                      const sal = row?.sal_list?.find(s => s.numero_sal === salNum) || {};
                      return (
                        <div key={salNum} className="bg-white rounded-lg p-3 border border-blue-200">
                          <div className="text-sm font-semibold text-blue-700 mb-2">SAL {salNum}</div>
                          <div className="space-y-2">
                            <div>
                              <label className="text-xs text-slate-500">%</label>
                              <Input
                                type="number"
                                value={sal.percentuale || ''}
                                onChange={(e) => handleUpdateSAL(salEditRowId, salNum - 1, 'percentuale', parseFloat(e.target.value) || null)}
                                className="h-7 text-xs"
                                placeholder="0"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-slate-500">Importo</label>
                              <Input
                                type="number"
                                value={sal.importo || ''}
                                onChange={(e) => handleUpdateSAL(salEditRowId, salNum - 1, 'importo', parseFloat(e.target.value) || null)}
                                className="h-7 text-xs"
                                placeholder="0"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-slate-500">Stato</label>
                              <Select
                                value={sal.stato || 'non_fatturabile'}
                                onValueChange={(value) => handleUpdateSAL(salEditRowId, salNum - 1, 'stato', value)}
                              >
                                <SelectTrigger className={`h-7 text-xs ${
                                  sal.stato === 'fatturato' ? 'bg-green-100 border-green-300' :
                                  sal.stato === 'da_fatturare' ? 'bg-yellow-100 border-yellow-300' :
                                  ''
                                }`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="non_fatturabile">Non Fatt.</SelectItem>
                                  <SelectItem value="da_fatturare">Da Fatt.</SelectItem>
                                  <SelectItem value="fatturato">Fatturato</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            {/* Campo Riferimento Fattura - visibile solo se da_fatturare o fatturato */}
                            {(sal.stato === 'da_fatturare' || sal.stato === 'fatturato') && (
                              <div>
                                <label className="text-xs text-orange-600">OdA/Rif. Fattura</label>
                                <Input
                                  type="text"
                                  value={sal.riferimento_fattura || ''}
                                  onChange={(e) => handleUpdateSAL(salEditRowId, salNum - 1, 'riferimento_fattura', e.target.value)}
                                  className="h-7 text-xs border-orange-300"
                                  placeholder="OdA, Rif..."
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Summary */}
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">
                  {editableRows.filter(r => r.selected).length} di {editableRows.length} righe selezionate
                  {editableRows.some(r => r._salCount > 0) && (
                    <span className="ml-2 text-blue-600">
                      (include {editableRows.reduce((acc, r) => acc + (r._salCount || 0), 0)} SAL totali)
                    </span>
                  )}
                </span>
                {editableRows.some(r => r.selected && r._hasError) && (
                  <span className="text-red-600">
                    <AlertCircle className="w-4 h-4 inline mr-1" />
                    Alcune righe richiedono un cliente
                  </span>
                )}
              </div>
              
              {/* Actions */}
              <div className="flex justify-between pt-4 border-t border-slate-200">
                <Button variant="outline" onClick={() => setImportStep(2)}>
                  Indietro
                </Button>
                <Button
                  onClick={handleExecuteImport}
                  disabled={importLoading || editableRows.filter(r => r.selected && r.cliente_id).length === 0}
                  className="bg-green-600 hover:bg-green-700"
                >
                  {importLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Importazione...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      Importa {editableRows.filter(r => r.selected && r.cliente_id).length} Ordini
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
          
          {/* Step 4: Results */}
          {importStep === 4 && importResult && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <Check className="w-8 h-8 text-green-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-green-700">{importResult.imported}</p>
                  <p className="text-sm text-green-600">Ordini Importati</p>
                </div>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
                  <X className="w-8 h-8 text-red-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-red-700">{importResult.errors?.length || 0}</p>
                  <p className="text-sm text-red-600">Errori</p>
                </div>
              </div>
              
              {/* Errors */}
              {importResult.errors && importResult.errors.length > 0 && (
                <div className="border border-red-200 rounded-lg overflow-hidden">
                  <div className="bg-red-50 px-4 py-2 border-b border-red-200">
                    <h4 className="font-semibold text-red-700">Errori</h4>
                  </div>
                  <div className="p-4 max-h-32 overflow-y-auto">
                    {importResult.errors.map((err, idx) => (
                      <p key={idx} className="text-sm text-red-600 py-1">
                        {err}
                      </p>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Imported Details */}
              {importResult.details && importResult.details.length > 0 && (
                <div className="border border-green-200 rounded-lg overflow-hidden">
                  <div className="bg-green-50 px-4 py-2 border-b border-green-200">
                    <h4 className="font-semibold text-green-700">Ordini Creati</h4>
                  </div>
                  <div className="p-4 max-h-48 overflow-y-auto">
                    {importResult.details.map((detail, idx) => (
                      <div key={idx} className="flex items-center justify-between py-2 border-b border-green-100 last:border-0">
                        <div>
                          <span className="font-mono font-semibold text-green-700">{detail.numero_ordine}</span>
                          <span className="mx-2 text-slate-400">|</span>
                          <span className="text-slate-700">{detail.nome_progetto}</span>
                        </div>
                        <span className="text-sm text-slate-500">{detail.cliente}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex justify-end pt-4 border-t border-slate-200">
                <Button
                  onClick={() => handleImportDialogClose(false)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Chiudi
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Detail Dialog with Allegati */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          {selectedOrdine && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl flex items-center justify-between">
                  <span className="flex items-center gap-3">
                    Ordine {selectedOrdine.numero_ordine} - {selectedOrdine.nome_progetto}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setDetailDialogOpen(false);
                      handleEdit(selectedOrdine);
                    }}
                    className="ml-4"
                  >
                    <Edit className="w-4 h-4 mr-2" />
                    Modifica
                  </Button>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-6 mt-4">
                {/* Informazioni Ordine */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 bg-slate-50 rounded-lg p-4">
                  <div>
                    <p className="text-sm text-slate-600">Cliente</p>
                    <p className="font-semibold">{selectedOrdine.cliente_nome}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">PM Main Solution</p>
                    <p className="font-semibold">{selectedOrdine.pm_main_solution_nome}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Importo Totale</p>
                    <p className="font-semibold text-green-600">{formatCurrency(selectedOrdine.importo_totale)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Importo Fatturato</p>
                    <p className="font-semibold text-blue-600">{formatCurrency(selectedOrdine.importo_fatturato)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Data Arrivo</p>
                    <p className="font-semibold">{new Date(selectedOrdine.data_arrivo_ordine).toLocaleDateString('it-IT')}</p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Referente Cliente</p>
                    <p className="font-semibold">{selectedOrdine.referente_cliente || '-'}</p>
                  </div>
                </div>

                {/* SAL List */}
                {selectedOrdine.sal_list && selectedOrdine.sal_list.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                      <Euro className="w-5 h-5" />
                      Stati Avanzamento Lavori (SAL)
                    </h3>
                    <div className="space-y-2">
                      {selectedOrdine.sal_list.map((sal, index) => (
                        <div key={index} className="bg-white border border-slate-200 rounded-lg p-4">
                          <div className="flex items-start justify-between">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 flex-1">
                              <div>
                                <p className="text-sm text-slate-600">SAL #{sal.numero_sal}</p>
                                <p className="font-semibold">{sal.descrizione || '-'}</p>
                              </div>
                              <div>
                                <p className="text-sm text-slate-600">Percentuale</p>
                                <p className="font-semibold">{sal.percentuale}%</p>
                              </div>
                              <div>
                                <p className="text-sm text-slate-600">Importo</p>
                                <p className="font-semibold">{formatCurrency(sal.importo)}</p>
                              </div>
                              <div>
                                <p className="text-sm text-slate-600">Stato Fatturazione</p>
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                                  sal.stato === 'fatturato' ? 'bg-green-100 text-green-700' :
                                  sal.stato === 'da_fatturare' ? 'bg-yellow-100 text-yellow-700' :
                                  'bg-slate-100 text-slate-600'
                                }`}>
                                  {sal.stato === 'fatturato' ? '✓ Fatturato' :
                                   sal.stato === 'da_fatturare' ? '⏳ Da Fatturare' :
                                   '− Non Fatturabile'}
                                </span>
                              </div>
                            </div>
                            {/* Pulsante Elimina SAL */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const salInfo = sal.descrizione 
                                  ? `SAL ${sal.numero_sal} - ${sal.descrizione}` 
                                  : `SAL ${sal.numero_sal}`;
                                handleDeleteSALFromDetails(selectedOrdine.id, index, salInfo);
                              }}
                              className="ml-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                              title="Elimina SAL"
                              data-testid={`delete-sal-${sal.numero_sal}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                          {/* Riferimento Fattura - visibile solo se da_fatturare o fatturato */}
                          {(sal.stato === 'da_fatturare' || sal.stato === 'fatturato') && (
                            <div className="mt-3 pt-3 border-t border-slate-100">
                              <p className="text-sm text-orange-600 font-medium">OdA o Riferimento Fattura</p>
                              <p className="font-semibold text-slate-800">
                                {sal.riferimento_fattura || <span className="text-slate-400 italic">Non specificato</span>}
                              </p>
                            </div>
                          )}
                          {/* Termini di Pagamento */}
                          <div className="mt-3 pt-3 border-t border-slate-100">
                            <p className="text-sm text-slate-600 font-medium">Termini di Pagamento</p>
                            <p className="font-semibold text-slate-800">
                              {sal.termini_pagamento_giorni || 30} giorni
                              {sal.fattura_fine_mese && (
                                <span className="ml-2 text-blue-600 text-sm font-normal">(da fine mese)</span>
                              )}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Allegati Section */}
                <div className="border-t pt-4">
                  <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
                    <Paperclip className="w-5 h-5" />
                    Allegati
                  </h3>

                  {/* Upload Form */}
                  <div className="bg-slate-50 rounded-lg p-4 mb-4">
                    <Label className="text-sm font-medium mb-2 block">Carica Nuovo Allegato</Label>
                    <div className="flex gap-2">
                      <Input
                        type="text"
                        placeholder="Descrizione allegato"
                        value={uploadDescrizione}
                        onChange={(e) => setUploadDescrizione(e.target.value)}
                        className="flex-1"
                      />
                      <Input
                        type="file"
                        onChange={(e) => setUploadFile(e.target.files[0])}
                        className="flex-1"
                      />
                      <Button
                        onClick={() => handleUploadAllegato(selectedOrdine.id)}
                        className="bg-blue-600 hover:bg-blue-700"
                        disabled={!uploadFile}
                      >
                        <Upload className="w-4 h-4 mr-2" />
                        Carica
                      </Button>
                    </div>
                  </div>

                  {/* Lista Allegati */}
                  {selectedOrdine.allegati && selectedOrdine.allegati.length > 0 ? (
                    <div className="space-y-2">
                      {selectedOrdine.allegati.map((allegato) => (
                        <div
                          key={allegato.id}
                          className="flex items-center justify-between p-3 bg-white border rounded-lg hover:bg-slate-50"
                        >
                          <div className="flex-1">
                            <p className="font-medium text-sm">{allegato.filename || allegato.nome_file || 'File senza nome'}</p>
                            {allegato.descrizione && (
                              <p className="text-xs text-slate-600">{allegato.descrizione}</p>
                            )}
                            <p className="text-xs text-slate-400">{new Date(allegato.uploaded_at).toLocaleDateString('it-IT')}</p>
                          </div>
                          <div className="flex gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadAllegato(selectedOrdine.id, allegato.id, allegato.filename || allegato.nome_file)}
                              className="hover:bg-blue-50 hover:text-blue-600"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteAllegato(selectedOrdine.id, allegato.id)}
                              className="hover:bg-red-50 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 text-center py-4">Nessun allegato presente</p>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-3 py-4 sm:px-6 sm:py-8">

        {/* Notifiche rimosse - ora sono in pagina dedicata /notifiche-progetti */}


        {/* Tabs */}
        <div className="bg-white rounded-t-xl border-x border-t border-slate-200 flex gap-1 p-2 mb-0">
          <button
            onClick={() => setActiveTab('ordini')}
            className={`px-4 py-2 font-medium rounded-lg transition-colors ${
              activeTab === 'ordini'
                ? 'bg-green-600 text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            📦 Ordini di Lavoro ({ordini.length})
          </button>
          <button
            onClick={() => setActiveTab('preventivi')}
            className={`px-4 py-2 font-medium rounded-lg transition-colors ${
              activeTab === 'preventivi'
                ? 'bg-amber-600 text-white'
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >
            📄 Preventivi da Convertire ({preventiviAccettati.length})
          </button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <p className="text-slate-600">Caricamento...</p>
          </div>
        ) : activeTab === 'preventivi' ? (
          /* Tabella Preventivi da Convertire */
          preventiviAccettati.length === 0 ? (
            <div className="bg-white rounded-b-xl shadow-sm border border-slate-200 p-12 text-center">
              <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-slate-900 mb-2">Nessun preventivo da convertire</h3>
              <p className="text-slate-600">I preventivi accettati appariranno qui</p>
            </div>
          ) : (
            <div className="bg-white rounded-b-xl shadow-sm border border-slate-200 overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold">N° Preventivo</TableHead>
                    <TableHead className="font-semibold">Cliente</TableHead>
                    <TableHead className="font-semibold">Descrizione</TableHead>
                    <TableHead className="font-semibold">Importo</TableHead>
                    <TableHead className="font-semibold">Data Emissione</TableHead>
                    <TableHead className="text-right font-semibold">Azioni</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {preventiviAccettati.map((preventivo) => (
                    <TableRow key={preventivo.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          {preventivo.numero_preventivo}
                          {preventivo.tipologia === 'accordo_quadro' && (
                            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
                              📋 AQ
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{preventivo.cliente_nome}</TableCell>
                      <TableCell>{preventivo.descrizione || '-'}</TableCell>
                      <TableCell>€ {preventivo.prezzo_manuale?.toLocaleString('it-IT')}</TableCell>
                      <TableCell>{new Date(preventivo.data_emissione).toLocaleDateString('it-IT')}</TableCell>
                      <TableCell className="text-right">
                        {preventivo.tipologia === 'accordo_quadro' ? (
                          <Button
                            size="sm"
                            onClick={() => {
                              // Redirect a AccordiQuadroPage con i dati del preventivo
                              navigate('/accordi-quadro', { 
                                state: { 
                                  fromPreventivo: true,
                                  preventivoData: {
                                    preventivo_id: preventivo.id,
                                    numero_preventivo: preventivo.numero_preventivo,
                                    cliente_id: preventivo.cliente_id,
                                    cliente_nome: preventivo.cliente_nome,
                                    importo: preventivo.prezzo_manuale,
                                    descrizione: preventivo.descrizione,
                                    note: preventivo.note
                                  }
                                }
                              });
                              toast.success(`Vai ad Accordi Quadro per completare la conversione`);
                            }}
                            className="bg-purple-600 hover:bg-purple-700"
                          >
                            <FolderKanban className="w-4 h-4 mr-2" />
                            Converti in Accordo Quadro
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => {
                              // Pre-compila form con dati preventivo
                              setFormData({
                                cliente_id: preventivo.cliente_id,
                                preventivo_id: preventivo.id,
                                referente_cliente: '',
                                pm_cliente: '',
                                codice_progetto_cliente: '',
                                codice_progetto_main_solution: '',
                                nome_progetto: preventivo.descrizione || '',
                                tipologia_lavorazione: preventivo.tipologia_lavorazione || '',
                                descrizione_attivita: preventivo.note || '',
                                data_arrivo_ordine: new Date().toISOString().split('T')[0],
                                data_conferma_ordine: '',
                                data_inizio_lavoro: '',
                                data_fine_lavoro: '',
                                pm_main_solution_id: '',
                                importo_totale: preventivo.prezzo_manuale || 0,
                                sal_list: [],
                                crea_progetto_automaticamente: false,
                                crea_cantiere_automaticamente: false
                              });
                              setDialogOpen(true);
                              toast.success(`Preventivo ${preventivo.numero_preventivo} caricato nel form`);
                            }}
                            className="bg-amber-600 hover:bg-amber-700"
                          >
                            <ArrowRight className="w-4 h-4 mr-2" />
                            Converti in Ordine
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )
        ) : ordini.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
            <Briefcase className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-900 mb-2">Nessun ordine presente</h3>
            <p className="text-slate-600 mb-6">Inizia creando il tuo primo ordine di lavoro</p>
            <Button
              onClick={() => setDialogOpen(true)}
              className="bg-green-600 hover:bg-green-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Nuovo Ordine
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Barra di ricerca e filtri */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
              <div className="flex flex-col gap-4">
                {/* Riga principale: Ricerca + Pulsante Filtri */}
                <div className="flex flex-wrap gap-3 items-center">
                  {/* Barra di ricerca */}
                  <div className="relative flex-1 min-w-[250px]">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
                    <Input
                      placeholder="Cerca per N° ordine, cliente, progetto..."
                      value={filters.searchText}
                      onChange={(e) => setFilters(prev => ({ ...prev, searchText: e.target.value }))}
                      className="pl-10"
                      data-testid="search-ordini"
                    />
                  </div>
                  
                  {/* Pulsante Filtri */}
                  <Button
                    variant={showFilters ? "default" : "outline"}
                    onClick={() => setShowFilters(!showFilters)}
                    className={showFilters ? "bg-green-600 hover:bg-green-700" : ""}
                    data-testid="toggle-filters"
                  >
                    <Filter className="w-4 h-4 mr-2" />
                    Filtri
                    {activeFiltersCount > 0 && (
                      <span className="ml-2 bg-white text-green-600 px-2 py-0.5 rounded-full text-xs font-bold">
                        {activeFiltersCount}
                      </span>
                    )}
                  </Button>
                  
                  {/* Export Excel */}
                  <Button
                    variant="outline"
                    onClick={handleExportExcel}
                    disabled={exportLoading || getFilteredOrdini().length === 0}
                    className="text-green-600 border-green-300 hover:bg-green-50"
                    data-testid="export-excel"
                  >
                    {exportLoading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    Export Excel
                  </Button>
                  
                  {/* Reset filtri */}
                  {activeFiltersCount > 0 && (
                    <Button
                      variant="ghost"
                      onClick={resetFilters}
                      className="text-slate-500 hover:text-slate-700"
                      data-testid="reset-filters"
                    >
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Reset
                    </Button>
                  )}
                  
                  {/* Conteggio risultati */}
                  <span className="text-sm text-slate-500">
                    {getFilteredOrdini().length} di {ordini.length} ordini
                  </span>
                </div>
                
                {/* Pannello filtri espandibile */}
                {showFilters && (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4 border-t border-slate-200">
                    {/* Filtro Cliente */}
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">Cliente</Label>
                      <Select
                        value={filters.clienteId}
                        onValueChange={(value) => setFilters(prev => ({ ...prev, clienteId: value === 'all' ? '' : value }))}
                      >
                        <SelectTrigger data-testid="filter-cliente">
                          <SelectValue placeholder="Tutti i clienti" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tutti i clienti</SelectItem>
                          {clienti.map(cliente => (
                            <SelectItem key={cliente.id} value={cliente.id}>
                              {cliente.nome_azienda}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Filtro PM Main Solution */}
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">Referente Main Solution</Label>
                      <Select
                        value={filters.pmId}
                        onValueChange={(value) => setFilters(prev => ({ ...prev, pmId: value === 'all' ? '' : value }))}
                      >
                        <SelectTrigger data-testid="filter-pm">
                          <SelectValue placeholder="Tutti i PM" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tutti i PM</SelectItem>
                          {utenti.filter(u => u.ruolo === 'admin' || u.ruolo === 'master' || userHasAmbito(u, 'ingegnere')).map(u => (
                            <SelectItem key={u.id} value={u.id}>
                              {u.nome} {u.cognome}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Filtro PM Cliente */}
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">PM Cliente</Label>
                      <Input
                        placeholder="Cerca PM Cliente..."
                        value={filters.pmClienteFilter}
                        onChange={(e) => setFilters(prev => ({ ...prev, pmClienteFilter: e.target.value }))}
                        data-testid="filter-pm-cliente"
                      />
                    </div>
                    
                    {/* Filtro Tipologia Lavorazione */}
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">Tipologia Lavorazione</Label>
                      <Select
                        value={filters.tipologiaLavorazione}
                        onValueChange={(value) => setFilters(prev => ({ ...prev, tipologiaLavorazione: value === 'all' ? '' : value }))}
                      >
                        <SelectTrigger data-testid="filter-tipologia">
                          <SelectValue placeholder="Tutte le tipologie" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tutte le tipologie</SelectItem>
                          {TIPOLOGIE_LAVORAZIONE.map(tipo => (
                            <SelectItem key={tipo} value={tipo}>{tipo}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Filtro Stato Fatturazione */}
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">Stato Fatturazione</Label>
                      <Select
                        value={filters.statoFatturazione}
                        onValueChange={(value) => setFilters(prev => ({ ...prev, statoFatturazione: value === 'all' ? '' : value }))}
                      >
                        <SelectTrigger data-testid="filter-stato">
                          <SelectValue placeholder="Tutti gli stati" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Tutti gli stati</SelectItem>
                          <SelectItem value="completato">✅ Completamente fatturato</SelectItem>
                          <SelectItem value="parziale">🟡 Parzialmente fatturato</SelectItem>
                          <SelectItem value="da_fatturare">🔴 Da fatturare</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Filtro Date */}
                    <div className="flex gap-2">
                      <div className="flex-1">
                        <Label className="text-xs text-slate-500 mb-1 block">Data da</Label>
                        <Input
                          type="date"
                          value={filters.dataInizio}
                          onChange={(e) => setFilters(prev => ({ ...prev, dataInizio: e.target.value }))}
                          data-testid="filter-data-inizio"
                        />
                      </div>
                      <div className="flex-1">
                        <Label className="text-xs text-slate-500 mb-1 block">Data a</Label>
                        <Input
                          type="date"
                          value={filters.dataFine}
                          onChange={(e) => setFilters(prev => ({ ...prev, dataFine: e.target.value }))}
                          data-testid="filter-data-fine"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
            
            {/* Tabella ordini */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              {getFilteredOrdini().length === 0 ? (
                <div className="p-12 text-center">
                  <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-slate-700 mb-2">Nessun ordine trovato</h3>
                  <p className="text-slate-500 mb-4">Nessun ordine corrisponde ai filtri selezionati</p>
                  <Button variant="outline" onClick={resetFilters}>
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Rimuovi filtri
                  </Button>
                </div>
              ) : (
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="font-semibold w-24">Azioni</TableHead>
                    <TableHead className="font-semibold">Cod. Main Solution</TableHead>
                    <TableHead className="font-semibold">Cod. Cliente</TableHead>
                    <TableHead className="font-semibold">Cliente</TableHead>
                    <TableHead className="font-semibold">Progetto</TableHead>
                    <TableHead className="font-semibold">Tipologia</TableHead>
                    <TableHead className="font-semibold">Descrizione</TableHead>
                    <TableHead className="font-semibold">Ref. Main Solution</TableHead>
                    <TableHead className="font-semibold">Importo</TableHead>
                    <TableHead className="font-semibold">Fatturato</TableHead>
                    <TableHead className="font-semibold">Stato</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {getFilteredOrdini().map((ordine) => (
                    <TableRow key={ordine.id} data-testid={`ordine-row-${ordine.numero_ordine}`}>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(ordine)}
                            data-testid={`edit-ordine-${ordine.numero_ordine}`}
                            className="hover:bg-green-50 hover:text-green-600 hover:border-green-300"
                            title="Modifica"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(ordine.id)}
                            data-testid={`delete-ordine-${ordine.numero_ordine}`}
                            className="hover:bg-red-50 hover:text-red-600"
                            title="Elimina"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          <span className="font-mono font-semibold text-green-600 flex items-center gap-2">
                            {ordine.codice_progetto_main_solution || '-'}
                            {ordiniConNotifiche.has(ordine.id) && (
                              <span className="text-red-500 text-lg animate-pulse" title="Progetto completato - Richiede fatturazione">
                                ⚠️
                              </span>
                            )}
                            {ordine.multiple_commesse && (
                              <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full font-medium" title="Ordine con Più Commesse">
                                MC
                              </span>
                            )}
                            {ordine.allegati && ordine.allegati.length > 0 && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setAllegatiQuickViewOrdine(ordine);
                                  setAllegatiQuickViewOpen(true);
                                }}
                                className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full font-medium flex items-center gap-1 hover:bg-blue-200 transition-colors cursor-pointer"
                                title={`Clicca per vedere ${ordine.allegati.length} allegato/i`}
                                data-testid={`allegati-badge-${ordine.numero_ordine}`}
                              >
                                <Paperclip className="w-3 h-3" />
                                {ordine.allegati.length}
                              </button>
                            )}
                          </span>
                          {ordine.progetto_numero && (
                            <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full font-medium w-fit">
                              PRJ {ordine.progetto_numero}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="font-mono text-sm text-slate-600">
                          {ordine.codice_progetto_cliente || '-'}
                        </span>
                      </TableCell>
                      <TableCell>{ordine.cliente_nome}</TableCell>
                      <TableCell className="font-medium">{ordine.nome_progetto}</TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-600">
                          {ordine.tipologia_lavorazione || '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-600 max-w-[200px] truncate block" title={ordine.descrizione_attivita}>
                          {ordine.descrizione_attivita || '-'}
                        </span>
                      </TableCell>
                      <TableCell className="text-sm">{ordine.pm_main_solution_nome}</TableCell>
                      <TableCell className="font-semibold">{formatCurrency(ordine.importo_totale)}</TableCell>
                      <TableCell>
                        <div className="flex flex-col text-xs">
                          <span className="text-green-600 font-semibold">
                            {formatCurrency(ordine.importo_fatturato)}
                          </span>
                          <span className="text-slate-500">
                            Da fatt.: {formatCurrency(ordine.importo_da_fatturare)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {(ordine.importo_da_fatturare || 0) > 0 ? (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-600">
                            Da completare
                          </span>
                        ) : (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-600">
                            Completato
                          </span>
                        )}
                      </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Dialog Aggiungi Descrizione SAL */}
      <Dialog open={dialogDescrizioneSAL.open} onOpenChange={(open) => setDialogDescrizioneSAL({ open, salIndex: null })}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="text-2xl">📝</span>
              Aggiungi Nuova Descrizione SAL
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="nuova-descrizione" className="text-sm font-medium">
                Descrizione
              </Label>
              <Input
                id="nuova-descrizione"
                placeholder="Es: Acconto 30%, SAL Intermedio, Saldo finale..."
                value={inputNuovaDescrizioneSAL}
                onChange={(e) => setInputNuovaDescrizioneSAL(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    // Trigger salva
                    document.getElementById('btn-salva-descrizione').click();
                  }
                }}
                className="mt-2"
                autoFocus
              />
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-800">
                💡 <strong>Suggerimento:</strong> Questa descrizione sarà salvata e disponibile per tutti i SAL futuri.
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => setDialogDescrizioneSAL({ open: false, salIndex: null })}
            >
              Annulla
            </Button>
            <Button
              id="btn-salva-descrizione"
              type="button"
              onClick={() => {
                const trimmed = inputNuovaDescrizioneSAL.trim();
                
                if (!trimmed) {
                  toast.error('Inserisci una descrizione valida');
                  return;
                }
                
                // Controlla se esiste già (case insensitive)
                const esiste = descrizioniSAL.some(d => d.toLowerCase() === trimmed.toLowerCase());
                
                if (esiste) {
                  toast.error('Questa descrizione esiste già');
                  return;
                }
                
                // Aggiungi la nuova descrizione
                const nuoveDescrizioni = [...descrizioniSAL, trimmed];
                
                // Aggiorna stato
                setDescrizioniSAL(nuoveDescrizioni);
                
                // Salva in localStorage
                localStorage.setItem('descrizioniSAL', JSON.stringify(nuoveDescrizioni));
                
                // Imposta nel campo corrente del SAL
                if (dialogDescrizioneSAL.salIndex !== null) {
                  handleSALChange(dialogDescrizioneSAL.salIndex, 'descrizione', trimmed);
                }
                
                toast.success(`✅ Descrizione "${trimmed}" aggiunta!`);
                
                // Chiudi dialog
                setDialogDescrizioneSAL({ open: false, salIndex: null });
                setInputNuovaDescrizioneSAL('');
              }}
              className="bg-green-600 hover:bg-green-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Salva Descrizione
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog Visualizzazione Rapida Allegati */}
      <Dialog open={allegatiQuickViewOpen} onOpenChange={setAllegatiQuickViewOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Paperclip className="w-5 h-5 text-blue-600" />
              Allegati - {allegatiQuickViewOrdine?.codice_progetto_main_solution || allegatiQuickViewOrdine?.numero_ordine}
            </DialogTitle>
          </DialogHeader>
          
          <div className="py-4">
            {allegatiQuickViewOrdine?.allegati && allegatiQuickViewOrdine.allegati.length > 0 ? (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {allegatiQuickViewOrdine.allegati.map((allegato) => (
                  <div
                    key={allegato.id}
                    className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-200 hover:bg-slate-100 transition-colors"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <FileText className="w-5 h-5 text-blue-500 flex-shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm truncate" title={allegato.filename || allegato.nome_file}>
                          {allegato.filename || allegato.nome_file || 'File senza nome'}
                        </p>
                        {allegato.descrizione && (
                          <p className="text-xs text-slate-500 truncate">{allegato.descrizione}</p>
                        )}
                        <p className="text-xs text-slate-400">
                          {new Date(allegato.uploaded_at).toLocaleDateString('it-IT', {
                            day: '2-digit',
                            month: 'short',
                            year: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadAllegato(allegatiQuickViewOrdine.id, allegato.id, allegato.filename || allegato.nome_file)}
                      className="ml-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-200"
                      data-testid={`download-allegato-${allegato.id}`}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-slate-500 text-center py-4">Nessun allegato presente</p>
            )}
          </div>

          <div className="flex justify-between items-center pt-2 border-t">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setAllegatiQuickViewOpen(false);
                handleViewDetails(allegatiQuickViewOrdine);
              }}
              className="text-slate-600"
            >
              <Eye className="w-4 h-4 mr-2" />
              Vai ai Dettagli Completi
            </Button>
            <Button
              variant="outline"
              onClick={() => setAllegatiQuickViewOpen(false)}
            >
              Chiudi
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrdiniLavoroPage;
